import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, vShow as _vShow, withDirectives as _withDirectives, Teleport as _Teleport } from "vue"
import _imports_0 from '@/assets/logos/sengrid.png'
import _imports_1 from '@/assets/logos/twilio.png'


const _hoisted_1 = {
  key: 0,
  class: "walkthrough-spotlight",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_2 = { id: "walkthrough-mask" }
const _hoisted_3 = ["x", "y", "width", "height"]
const _hoisted_4 = {
  key: 1,
  class: "walkthrough-backdrop"
}
const _hoisted_5 = {
  key: 0,
  class: "sc"
}
const _hoisted_6 = { class: "sc-particles" }
const _hoisted_7 = { class: "sc-pipeline" }
const _hoisted_8 = { class: "sc-stage-card sc-card-green sc-phone-card" }
const _hoisted_9 = { class: "sc-mobile-story" }
const _hoisted_10 = { class: "sc-m-card sc-m-green" }
const _hoisted_11 = { class: "sc-m-bubbles" }
const _hoisted_12 = {
  key: 1,
  class: "ig-showcase"
}
const _hoisted_13 = { class: "ig-services" }
const _hoisted_14 = {
  key: 2,
  class: "lf-showcase"
}
const _hoisted_15 = { class: "lf-browser" }
const _hoisted_16 = { class: "lf-viewport" }
const _hoisted_17 = {
  key: 3,
  class: "fp-showcase"
}
const _hoisted_18 = { class: "sc-particles" }
const _hoisted_19 = {
  key: 4,
  class: "welcome-showcase"
}
const _hoisted_20 = { class: "wc-meta" }
const _hoisted_21 = {
  key: 0,
  class: "wc-meta-pill"
}
const _hoisted_22 = { class: "tooltip-icon" }
const _hoisted_23 = { class: "tooltip-content" }
const _hoisted_24 = { class: "tooltip-title" }
const _hoisted_25 = { class: "tooltip-body" }
const _hoisted_26 = { class: "objective-check" }
const _hoisted_27 = { class: "objective-text" }
const _hoisted_28 = { class: "tooltip-actions" }
const _hoisted_29 = {
  key: 0,
  class: "key-hint"
}
const _hoisted_30 = { class: "tooltip-counter" }
const _hoisted_31 = { class: "confetti-container" }
const _hoisted_32 = {
  key: 0,
  class: "offtrack-pill"
}
const _hoisted_33 = {
  key: 0,
  class: "wt-generating-overlay"
}

import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useWalkthroughStore } from '@/store/walkthroughStore';
import { useDemoStore } from '@/store/demoStore';
import { useAccessStore } from '@/store/accessStore';
import {
  streamWalkthroughAI,
  type WalkthroughAISummary,
  type WalkthroughAIMessageChange
} from '@/api/messages';
import WalkthroughSimulate from '@/components/WalkthroughSimulate.vue';
import DemoCelebrationV3 from '@/components/DemoCelebrationV3.vue';
import WalkthroughCelebration from '@/components/WalkthroughCelebration.vue';
import { getProject, setActiveStatus, updateProject } from '@/api/projects';
import {
  aiGenerateForm,
  aiGenerateWalkthroughForms,
  resetWalkthroughForms,
  getLeadForms,
  deleteLeadForm,
  updateLeadForm
} from '@/api/forms';
import type { WalkthroughFormPair } from '@/api/forms';

const CLAMP_PAD = 0;
const MIN_ACTIONS = 3;

export default /*@__PURE__*/_defineComponent({
  __name: 'DemoWalkthrough',
  setup(__props) {

const store = useWalkthroughStore();
const demoStore = useDemoStore();
const accessStore = useAccessStore();
const router = useRouter();
const route = useRoute();

const spotlightRect = ref<DOMRect | null>(null);
const pinnedTooltipTop = ref<number | null>(null); // Lock tooltip vertical pos once placed
const tooltipReady = ref(true);
const modalOpen = ref(false); // True when a page modal (.modal-overlay) is open
let modalObserver: MutationObserver | null = null;
const showCelebration = ref(false);
const showcaseStep = ref(0);
let showcaseTimer: ReturnType<typeof setInterval> | null = null;
const lfActiveType = ref<'popup' | 'slidein' | 'banner'>('popup');
let lfCycleTimer: ReturnType<typeof setInterval> | null = null;
const simRef = ref<InstanceType<typeof WalkthroughSimulate> | null>(null);

// ── Stop walkthrough if user loses access ──
watch(
  () => accessStore.showNoAccess,
  (denied) => {
    if (denied && store.isActive) {
      console.log('[walkthrough] Access denied — dismissing walkthrough');
      store.dismiss();
    }
  }
);

// ── Celebration: project active status (non-demo) ──
const projectIsActive = ref<boolean | null>(null);
const activatingProject = ref(false);
const activationSuccess = ref(false);
const celebrationLoading = ref(false);
const celebrationStage = ref(0);
let celebrationTimer: ReturnType<typeof setTimeout>[] = [];

function startCelebrationAnimation() {
  stopCelebrationAnimation();
  celebrationStage.value = 0;
  const delays = [100, 500, 950, 1400, 1850];
  delays.forEach((ms, i) => {
    const t = setTimeout(() => {
      celebrationStage.value = i + 1;
    }, ms);
    celebrationTimer.push(t);
  });
}

function stopCelebrationAnimation() {
  celebrationTimer.forEach(clearTimeout);
  celebrationTimer = [];
  celebrationStage.value = 0;
}

// ── Cached walkthrough AI results (from SSE stream) ──
const cachedSummaries = ref<WalkthroughAISummary[] | null>(null);
const cachedPreviousInsight = ref<string | null>(null);

// ── Walkthrough forms picker ──
const walkthroughFormsLoading = ref(false);
const walkthroughFormPairs = ref<WalkthroughFormPair[]>([]);
const walkthroughShopName = ref<string | null>(null);
let walkthroughPickerWindow: Window | null = null;
let formsPrefetchPromise: Promise<void> | null = null;

/** Prefetch walkthrough forms + shopName in the background.
 *  Returns a promise that resolves when both are loaded.
 *  Safe to call multiple times — subsequent calls return the same promise. */
function prefetchWalkthroughForms(): Promise<void> {
  if (formsPrefetchPromise) return formsPrefetchPromise;
  if (demoStore.isDemoMode || !store.projectId) return Promise.resolve();
  if (store.walkthroughFormsPicked) return Promise.resolve();

  const projectId = store.projectId;
  if (!projectId) return Promise.resolve();

  formsPrefetchPromise = (async () => {
    // Fetch shopName + sync backend flags
    if (!walkthroughShopName.value) {
      try {
        const project = await getProject(projectId);
        walkthroughShopName.value = project.shopName || null;
        if (
          (project as unknown as Record<string, unknown>)
            .walkthroughFormsGenerated
        ) {
          store.walkthroughFormsGenerated = true;
        }
        if (
          (project as unknown as Record<string, unknown>).walkthroughFormsPicked
        ) {
          store.walkthroughFormsPicked = true;
          store.persistState();
        }
      } catch {
        console.warn(
          '[walkthrough] Prefetch: could not fetch project for shopName'
        );
      }
    }

    // Fetch walkthrough form pairs
    if (!walkthroughFormPairs.value.length) {
      if (!store.walkthroughFormsGenerated) {
        walkthroughFormsLoading.value = true;
      }
      try {
        console.log('[walkthrough] Prefetching walkthrough form pairs…');
        const resp = await aiGenerateWalkthroughForms(projectId);
        if (resp.success) {
          walkthroughFormPairs.value = resp.pairs;
          store.walkthroughFormsGenerated = true;
          store.persistState();
          console.log(
            '[walkthrough] Walkthrough forms prefetched:',
            resp.forms.length
          );
        } else {
          console.warn(
            '[walkthrough] Walkthrough forms had errors:',
            resp.errors
          );
        }
      } catch (err) {
        console.warn(
          '[walkthrough] Failed to prefetch walkthrough forms:',
          err
        );
      } finally {
        walkthroughFormsLoading.value = false;
      }
    }

    // Also generate the sandbox form if needed
    if (!store.sandboxFormGenerated && projectId) {
      try {
        console.log('[walkthrough] Generating sandbox lead form…');
        await aiGenerateForm(
          projectId,
          'Create a stylish newsletter signup popup form. Include a catchy headline, a short description about exclusive deals and updates, an email field, and a submit button. Use a warm, inviting tone.'
        );
        store.sandboxFormGenerated = true;
        store.persistState();
        console.log('[walkthrough] Sandbox form generated & saved');
      } catch (err) {
        console.warn('[walkthrough] Failed to generate sandbox form:', err);
      }
    }
  })();
  return formsPrefetchPromise;
}

/** Open the user's store with the walkthrough picker mode.
 *  Must be synchronous (no await before window.open) to avoid popup blockers. */
function openWalkthroughPicker() {
  if (!store.projectId) return;

  if (!walkthroughShopName.value) {
    console.warn('[walkthrough] No shopName — cannot open picker');
    return;
  }

  // Collect form IDs from pairs to pass as URL param
  const formIds: string[] = [];
  for (const pair of walkthroughFormPairs.value) {
    if (pair.a) formIds.push(pair.a.id);
    if (pair.b) formIds.push(pair.b.id);
  }

  const url = `https://${
    walkthroughShopName.value
  }.myshopify.com?p1_walkthrough_pick=${formIds.join(',')}`;
  walkthroughPickerWindow = window.open(url, '_blank');
}

/** Handle postMessage from the storefront picker */
async function handlePickerMessage(event: MessageEvent) {
  const data = event.data;
  if (!data || data.type !== 'myuser-walkthrough-form-picks') return;
  if (!store.projectId) return;

  console.log('[walkthrough] Received picker selections:', data.picks);

  // data.picks = { popup: 'formId' | null, slide_in: 'formId' | null, banner: 'formId' | null }
  const picks: Record<string, string | null> = data.picks || {};
  const deletePromises: Promise<unknown>[] = [];
  const updatePromises: Promise<unknown>[] = [];

  // Persist only explicitly chosen forms.
  for (const pair of walkthroughFormPairs.value) {
    const chosenId = picks[pair.type];
    const formA = pair.a;
    const formB = pair.b;
    const formsInPair = [formA, formB].filter(
      (form): form is NonNullable<typeof form> => Boolean(form)
    );

    if (!formsInPair.length) continue;

    if (chosenId) {
      const chosenForm = formsInPair.find((form) => form?.id === chosenId);
      const unchosenForms = formsInPair.filter((form) => form?.id !== chosenId);

      if (chosenForm) {
        updatePromises.push(
          updateLeadForm(store.projectId, chosenForm.id, {
            tags: (chosenForm.tags || []).filter(
              (tag) =>
                tag === 'walkthrough-origin' ||
                (tag !== 'walkthrough' && !tag.startsWith('walkthrough-'))
            )
          }).catch((err) => {
            console.warn(
              '[walkthrough] Failed to clean chosen form tags:',
              err
            );
          })
        );
      }

      for (const unchosen of unchosenForms) {
        deletePromises.push(
          deleteLeadForm(store.projectId, unchosen.id).catch((err) => {
            console.warn('[walkthrough] Failed to delete unchosen form:', err);
          })
        );
      }
    } else {
      // Nothing picked for this type — delete both generated walkthrough forms.
      console.log(
        `[walkthrough] Type ${pair.type} skipped — deleting both walkthrough variants`
      );
      for (const form of formsInPair) {
        deletePromises.push(
          deleteLeadForm(store.projectId, form.id).catch((err) => {
            console.warn('[walkthrough] Failed to delete skipped form:', err);
          })
        );
      }
    }
  }

  await Promise.all(updatePromises);
  await Promise.all(deletePromises);

  // Mark forms as picked (tenant-level) so subsequent users skip the picker
  store.walkthroughFormsPicked = true;
  store.persistState();
  if (store.projectId) {
    updateProject(store.projectId, {
      walkthroughFormsPicked: true
    } as any).catch((err: unknown) =>
      console.warn('[walkthrough] Failed to sync formsPicked:', err)
    );
  }

  // Refresh the forms view immediately so the chosen forms appear on step 16.
  window.dispatchEvent(new CustomEvent('walkthrough-refresh-forms'));

  // Close the picker window if still open
  if (walkthroughPickerWindow && !walkthroughPickerWindow.closed) {
    walkthroughPickerWindow.close();
  }
  walkthroughPickerWindow = null;

  // Advance to next step
  store.next();
}

const cachedNewInsight = ref<string | null>(null);
const cachedMessages = ref<WalkthroughAIMessageChange[] | null>(null);
const walkthroughAILoading = ref(false);
// Timer ID for the message poll (cleared on step change to prevent stale dispatches)
let messagePollTimer: ReturnType<typeof setInterval> | null = null;
let walkthroughRefreshRequestCounter = 0;
// Pre-walkthrough session IDs — cached so revisits can restore "before" state
const cachedPreSessionIds = ref<string[] | null>(null);
function handleSessionIdsCaptured(e: Event) {
  const ids = (e as CustomEvent).detail?.sessionIds;
  if (ids && !cachedPreSessionIds.value) {
    // Only capture once — on the first run, before walkthrough creates new sessions
    cachedPreSessionIds.value = ids;
  }
}

function getNewestWalkthroughSession(
  summaries: WalkthroughAISummary[] | null | undefined = cachedSummaries.value
) {
  const preIds = new Set(cachedPreSessionIds.value || []);
  const candidates = (summaries || []).filter(
    (summary) => summary.sessionId && !preIds.has(summary.sessionId)
  );
  const walkthroughCandidates = candidates.filter((summary) =>
    summary.sessionId.startsWith('wt-session-')
  );
  const pool =
    walkthroughCandidates.length > 0 ? walkthroughCandidates : candidates;

  return (
    [...pool].sort((a, b) => {
      const aTime = new Date(a.endTime || a.startTime || 0).getTime();
      const bTime = new Date(b.endTime || b.startTime || 0).getTime();
      return bTime - aTime;
    })[0] || null
  );
}

async function requestWalkthroughRefresh(timeoutMs = 5000) {
  const requestId = `walkthrough-refresh-${++walkthroughRefreshRequestCounter}`;

  return new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener(
        'walkthrough-refresh-complete',
        handleComplete
      );
      if (timer) clearTimeout(timer);
    };

    const handleComplete = (e: Event) => {
      const completedRequestId = (e as CustomEvent).detail?.requestId;
      if (completedRequestId !== requestId) return;
      cleanup();
      resolve();
    };

    window.addEventListener('walkthrough-refresh-complete', handleComplete);
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);

    window.dispatchEvent(
      new CustomEvent('walkthrough-refresh', {
        detail: { requestId }
      })
    );
  });
}
// ── Interactive target boost (z-index above overlay) ──
let interactiveEl: HTMLElement | null = null;
let spotlightObserver: ResizeObserver | null = null;
let spotlightTargetEl: Element | null = null;
let spotlightScrollParent: Element | null = null;
let spotlightRafId: number | null = null;
let fallbackMutationObserver: MutationObserver | null = null;
let spotlightRecoveryTimer: ReturnType<typeof setInterval> | null = null;
let scrollLockActive = false;

function preventScrollHandler(e: Event) {
  e.preventDefault();
}

function enableScrollLock() {
  if (scrollLockActive) return;
  scrollLockActive = true;
  const opts = { passive: false } as const;
  window.addEventListener('wheel', preventScrollHandler, opts);
  window.addEventListener('touchmove', preventScrollHandler, opts);
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.addEventListener('wheel', preventScrollHandler, opts);
    appContainer.addEventListener('touchmove', preventScrollHandler, opts);
  }
}

function disableScrollLock() {
  if (!scrollLockActive) return;
  scrollLockActive = false;
  window.removeEventListener('wheel', preventScrollHandler);
  window.removeEventListener('touchmove', preventScrollHandler);
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.removeEventListener('wheel', preventScrollHandler);
    appContainer.removeEventListener('touchmove', preventScrollHandler);
  }
}

function stopFallbackWatch() {
  if (fallbackMutationObserver) {
    fallbackMutationObserver.disconnect();
    fallbackMutationObserver = null;
  }
}

function stopSpotlightTracking() {
  if (spotlightObserver) {
    spotlightObserver.disconnect();
    spotlightObserver = null;
  }
  if (spotlightScrollParent) {
    spotlightScrollParent.removeEventListener('scroll', onSpotlightScroll);
    spotlightScrollParent.removeEventListener('scroll', onScrollClamp);
    // Remove the walkthrough padding we added
    (spotlightScrollParent as HTMLElement).style.removeProperty(
      'padding-bottom'
    );
    (spotlightScrollParent as HTMLElement).style.removeProperty('padding-top');
    spotlightScrollParent = null;
  }
  window.removeEventListener('scroll', onSpotlightScroll, true);
  window.removeEventListener('scroll', onScrollClampWindow, true);
  // Clean up mobile .app-container listeners
  const appContainer = document.querySelector(
    '.app-container'
  ) as HTMLElement | null;
  if (appContainer) {
    appContainer.removeEventListener('scroll', onSpotlightScroll);
    appContainer.removeEventListener('scroll', onScrollClampWindow);
  }
  if (spotlightRafId !== null) {
    cancelAnimationFrame(spotlightRafId);
    spotlightRafId = null;
  }
  spotlightTargetEl = null;
  if (spotlightRecoveryTimer !== null) {
    clearInterval(spotlightRecoveryTimer);
    spotlightRecoveryTimer = null;
  }
  stopFallbackWatch();
}

function refreshSpotlightRect() {
  if (!spotlightTargetEl) return;

  // If the element is still in the DOM, always use it — even if temporarily
  // zero-sized (e.g. mid-transition). This prevents snapping to the wrong element.
  if (spotlightTargetEl.isConnected) {
    const rect = spotlightTargetEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      spotlightRect.value = rect;
    }
    // Zero-sized but still connected: keep current rect, don't re-search
    return;
  }

  // Element is detached — try to re-find by step target selector
  const step = store.currentStep;
  if (!step?.target) return;

  let newEl: Element | null = null;

  // For session-summary, target the specific new session by data-session-id
  // to avoid snapping to an older session summary after DOM rebuild.
  // NEVER fall back to the generic .session-summary-item selector — that
  // would match the first (oldest) session in the DOM.
  if (step.id === 'session-summary') {
    const newSessionId = getNewestWalkthroughSession()?.sessionId;
    if (newSessionId) {
      newEl = document.querySelector(
        `.session-summary-item[data-session-id="${newSessionId}"]`
      );
    }
    if (!newEl) {
      newEl = document.querySelector('.session-skeleton-item');
    }
    // If neither found, DON'T fall through to generic selector
  } else {
    newEl = document.querySelector(step.target);
  }

  if (
    newEl &&
    newEl !== spotlightTargetEl &&
    (newEl as HTMLElement).offsetParent !== null
  ) {
    const newRect = newEl.getBoundingClientRect();
    if (newRect.width > 0 && newRect.height > 0) {
      console.log('[spotlight] Re-acquired target after DOM rebuild');
      startSpotlightTracking(newEl);
      spotlightRect.value = newRect;
    }
  }
}

/**
 * When any scroll fires, use a rAF loop to smoothly track the element
 * until scrolling settles (position stops changing between frames).
 */
let rafScrolling = false;
function onSpotlightScroll() {
  if (rafScrolling) return;
  rafScrolling = true;
  let prevTop = -1;
  let settledFrames = 0;
  const track = () => {
    if (!spotlightTargetEl) {
      rafScrolling = false;
      return;
    }
    refreshSpotlightRect();
    const curTop = spotlightTargetEl.getBoundingClientRect().top;
    if (Math.abs(curTop - prevTop) < 0.5) {
      settledFrames++;
    } else {
      settledFrames = 0;
    }
    prevTop = curTop;
    // Stop after position is stable for 5 consecutive frames (~80ms)
    if (settledFrames < 5) {
      spotlightRafId = requestAnimationFrame(track);
    } else {
      rafScrolling = false;
      spotlightRafId = null;
    }
  };
  spotlightRafId = requestAnimationFrame(track);
}

/**
 * Clamp scroll on the timeline container so the spotlight target
 * can't be scrolled out of the container's visible area.
 * Uses the scroll parent's own bounding rect, not the full viewport.
 * Keeps the ENTIRE element visible (with padding) if it fits.
 */
let clampingScroll = false;
function onScrollClamp() {
  if (clampingScroll || !spotlightTargetEl || !spotlightScrollParent) return;
  const elRect = spotlightTargetEl.getBoundingClientRect();
  const containerRect = spotlightScrollParent.getBoundingClientRect();

  const visibleTop = containerRect.top + CLAMP_PAD;
  // On mobile, the bottom-pinned tooltip covers the lower portion of the
  // viewport. Shrink the visible area so the spotlight target can't scroll
  // behind it.
  let visibleBottom = containerRect.bottom - CLAMP_PAD;
  if (isMobileViewport()) {
    const tooltipReserve = Math.min(window.innerHeight * 0.45, 260);
    visibleBottom = Math.min(
      visibleBottom,
      window.innerHeight - tooltipReserve
    );
  }
  const visibleH = visibleBottom - visibleTop;

  // If element fits within the visible area, keep it entirely visible
  if (elRect.height <= visibleH) {
    if (elRect.top < visibleTop) {
      // Element's top is above visible area — scroll up
      clampingScroll = true;
      spotlightScrollParent.scrollTop -= visibleTop - elRect.top;
      clampingScroll = false;
    } else if (elRect.bottom > visibleBottom) {
      // Element's bottom is below visible area — scroll down
      clampingScroll = true;
      spotlightScrollParent.scrollTop += elRect.bottom - visibleBottom;
      clampingScroll = false;
    }
  } else {
    // Element is taller than visible area — keep at least top visible
    if (elRect.top > visibleTop + 40) {
      clampingScroll = true;
      spotlightScrollParent.scrollTop += elRect.top - (visibleTop + 40);
      clampingScroll = false;
    } else if (elRect.bottom < visibleTop + 80) {
      clampingScroll = true;
      spotlightScrollParent.scrollTop -= visibleTop + 80 - elRect.bottom;
      clampingScroll = false;
    }
  }
}

/**
 * Clamp window-level scroll so spotlight target stays in viewport.
 * On mobile, account for the bottom-pinned tooltip taking up space.
 */
function onScrollClampWindow() {
  if (clampingScroll || !spotlightTargetEl) return;
  // Only clamp window scroll when there's no scroll-parent container
  // (i.e. for page-level targets like dashboard items)
  if (spotlightScrollParent) return;
  const rect = spotlightTargetEl.getBoundingClientRect();
  const elH = rect.height;
  const vpH = window.innerHeight;
  // On mobile, the tooltip takes ~40% of the bottom — restrict visible area
  const bottomReserved = isMobileViewport() ? Math.min(vpH * 0.4, 240) : 60;
  // On mobile, account for progress bar below navbar
  const topReserved = isMobileViewport() ? 56 + 30 : 80;
  const minVisible = Math.min(60, elH);

  // On mobile, .app-container is the scroll container (overflow-y: auto),
  // not the window. Detect it and scrollBy on the right element.
  const scroller = isMobileViewport()
    ? (document.querySelector('.app-container') as HTMLElement) || window
    : window;
  const doScroll = (dy: number) => {
    if (scroller === window) window.scrollBy(0, dy);
    else (scroller as HTMLElement).scrollTop += dy;
  };

  if (rect.bottom < topReserved + minVisible) {
    const overshoot = topReserved + minVisible - rect.bottom;
    clampingScroll = true;
    doScroll(-overshoot);
    clampingScroll = false;
  } else if (rect.top > vpH - bottomReserved - minVisible) {
    const overshoot = rect.top - (vpH - bottomReserved - minVisible);
    clampingScroll = true;
    doScroll(overshoot);
    clampingScroll = false;
  }
}

function startSpotlightTracking(el: Element) {
  stopSpotlightTracking();
  spotlightTargetEl = el;

  // Watch for size changes (typewriter, content expansion, etc.)
  // Throttled to avoid jitter from rapid typewriter updates (8ms/char)
  let resizeRafId: number | null = null;
  spotlightObserver = new ResizeObserver(() => {
    if (resizeRafId) return; // already scheduled
    resizeRafId = requestAnimationFrame(() => {
      resizeRafId = null;
      refreshSpotlightRect();
      // When element grows (e.g. typewriter, expanding events), keep it in view
      if (spotlightScrollParent && spotlightTargetEl) {
        const elRect = spotlightTargetEl.getBoundingClientRect();
        const cRect = spotlightScrollParent.getBoundingClientRect();
        // If element bottom is now below the container, scroll to keep it visible
        if (elRect.bottom > cRect.bottom) {
          clampingScroll = true;
          spotlightScrollParent.scrollTop += elRect.bottom - cRect.bottom;
          clampingScroll = false;
          refreshSpotlightRect();
        }
      }
    });
  });
  spotlightObserver.observe(el);

  // Watch for scroll in parent containers (timeline scroll)
  const scrollParent = el.closest('.timeline-content') as HTMLElement | null;
  if (scrollParent) {
    spotlightScrollParent = scrollParent;
    // Add top + bottom padding so items anywhere can scroll to center
    const containerH = scrollParent.clientHeight;
    scrollParent.style.paddingTop = containerH / 3 + 'px';
    scrollParent.style.paddingBottom = containerH / 2 + 'px';
    scrollParent.addEventListener('scroll', onSpotlightScroll, {
      passive: true
    });
    // Clamp scroll so highlighted element can't leave container
    scrollParent.addEventListener('scroll', onScrollClamp);
  }

  // Also track window/document-level scroll (page-level scrollIntoView)
  window.addEventListener('scroll', onSpotlightScroll, {
    passive: true,
    capture: true
  });
  window.addEventListener('scroll', onScrollClampWindow, { capture: true });

  // On mobile, .app-container is the real scroll container (overflow-y: auto).
  // Attach scroll listeners there too so spotlight tracks during dashboard scroll.
  if (isMobileViewport()) {
    const appContainer = document.querySelector(
      '.app-container'
    ) as HTMLElement | null;
    if (appContainer) {
      appContainer.addEventListener('scroll', onSpotlightScroll, {
        passive: true
      });
      appContainer.addEventListener('scroll', onScrollClampWindow);
    }
  }

  // Periodic check: if the DOM element gets detached (e.g. Vue re-render from
  // loadTimelineData), the ResizeObserver stops firing. Poll to detect this
  // and trigger auto-recovery in refreshSpotlightRect.
  if (spotlightRecoveryTimer !== null) clearInterval(spotlightRecoveryTimer);
  spotlightRecoveryTimer = setInterval(() => {
    if (spotlightTargetEl && !spotlightTargetEl.isConnected) {
      console.log('[spotlight] Tracked element detached, attempting recovery');
      refreshSpotlightRect();
    }
  }, 300);
}

function cleanupInteractive() {
  if (interactiveEl) {
    interactiveEl.classList.remove('walkthrough-interactive-target');
    interactiveEl.style.removeProperty('position');
    interactiveEl.style.removeProperty('z-index');
    interactiveEl = null;
  }
}

// ── Simulate step button logic ──
const simDoneCount = computed(() => simRef.value?.doneCount ?? 0);
// Cached count: survives simRef.reset() so session-loading event gets the real number
const lastSimDoneCount = ref(0);
async function simBackToMenu() {
  if (!simRef.value) return;
  // If user typed a search query, submit it before going back to menu
  if (simRef.value.view === 'search' && simRef.value.searchInput?.trim()) {
    await simRef.value.submitSearch();
    return; // submitSearch already sets view back to 'menu'
  }
  // If user filled out a form, submit it before going back
  if (simRef.value.view === 'form' && simRef.value.hasAnyAnswer) {
    await simRef.value.submitForm();
    return; // submitForm already sets view back to 'menu'
  }
  simRef.value.view = 'menu';
}
const simIsInSubView = computed(
  () =>
    store.currentStep?.showcase === 'simulate-interactive' &&
    simRef.value?.view !== 'menu'
);
const simStepNeedsMore = computed(
  () =>
    store.currentStep?.showcase === 'simulate-interactive' &&
    simDoneCount.value < MIN_ACTIONS
);
const simActionLabel = computed(() => {
  if (store.currentStep?.showcase === 'simulate-interactive') {
    if (simDoneCount.value < MIN_ACTIONS) {
      return `Try ${MIN_ACTIONS - simDoneCount.value} more action${
        MIN_ACTIONS - simDoneCount.value === 1 ? '' : 's'
      }`;
    }
    return 'See what happened →';
  }
  return store.currentStep?.actionLabel || 'Next →';
});

// ── Range drag objective ──
const rangeDragged = ref(false);
function onRangeDragged() {
  rangeDragged.value = true;
}
onMounted(() => {
  window.addEventListener('range-dragged', onRangeDragged);
});
onUnmounted(() => {
  window.removeEventListener('range-dragged', onRangeDragged);
});
// Reset when step changes
watch(
  () => store.currentStepIndex,
  () => {
    rangeDragged.value = false;
  }
);

// ── Off-track detection ──
const isTransitioning = ref(false);
const isClickThroughAdvanceInProgress = ref(false);

/** True when the current step requires a userID but one hasn't been generated yet */
const awaitingUserGeneration = computed(() => {
  if (!store.isActive || !store.currentStep) return false;
  const resolvedRoute = store.currentStep.route;
  if (!resolvedRoute) return false;
  // The resolved route will have empty segments (e.g. /user//PROJECT) when targetUserId is null
  return resolvedRoute.includes('//');
});

const isOffTrack = computed(() => {
  if (isTransitioning.value) return false;
  if (awaitingUserGeneration.value) return false;
  if (!store.isActive || !store.currentStep?.route) return false;

  // During the customers click-through step, navigating to any user page is expected
  if (
    store.currentStep.id === 'customers-table' &&
    route.path.startsWith('/user/')
  ) {
    return false;
  }

  const stepRoute = store.currentStep.route;
  if (route.path === stepRoute) return false;
  // Compare route prefixes (e.g. /dashboard vs /user)
  const stepPrefix = stepRoute.split('/').slice(0, 2).join('/');
  const currentPrefix = route.path.split('/').slice(0, 2).join('/');
  return stepPrefix !== currentPrefix || stepPrefix.length <= 1;
});

const offTrackPageName = computed(() => {
  const r = store.currentStep?.route || '';
  if (r.includes('/dashboard')) return 'Dashboard';
  if (r.includes('/user/')) return 'Customer Timeline';
  if (r.includes('/templates')) return 'Templates';
  if (r.includes('/forms')) return 'Forms';
  if (r.includes('/results')) return 'Results';
  if (r.includes('/simulate')) return 'Simulate';
  if (r.includes('/organization')) return 'Organization';
  if (r.includes('/integrations')) return 'Integrations';
  return 'the tour';
});

async function returnToTour() {
  await navigateIfNeeded();
  await nextTick();
  await new Promise((r) => setTimeout(r, 400));
  await updateSpotlight();
}

function startShowcaseAnimation() {
  stopShowcaseAnimation();
  stopLfCycle();
  showcaseStep.value = 0;
  lfActiveType.value = 'popup';
  showcaseTimer = setInterval(() => {
    showcaseStep.value++;
    if (showcaseStep.value === 3) {
      startLfCycle();
    }
    if (showcaseStep.value >= 6) {
      stopShowcaseAnimation();
    }
  }, 540);
}

function stopShowcaseAnimation() {
  if (showcaseTimer) {
    clearInterval(showcaseTimer);
    showcaseTimer = null;
  }
}

function stopLfCycle() {
  if (lfCycleTimer) {
    clearInterval(lfCycleTimer);
    lfCycleTimer = null;
  }
}

function startLfCycle() {
  if (lfCycleTimer) return;
  const types: Array<'popup' | 'slidein' | 'banner'> = [
    'popup',
    'slidein',
    'banner'
  ];
  let idx = 0;
  lfCycleTimer = setInterval(() => {
    idx = (idx + 1) % types.length;
    lfActiveType.value = types[idx];
  }, 2800);
}

function pickLfType(t: 'popup' | 'slidein' | 'banner') {
  lfActiveType.value = t;
  // restart cycle from this choice
  if (lfCycleTimer) {
    clearInterval(lfCycleTimer);
    lfCycleTimer = null;
  }
  startLfCycle();
}

// ── Confetti style generator ──
const CONFETTI_COLORS = [
  '#6366f1',
  '#a855f7',
  '#ec4899',
  '#fbbf24',
  '#34d399',
  '#60a5fa'
];
function confettiStyle(i: number) {
  const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 2;
  const duration = 2 + Math.random() * 2;
  const size = 6 + Math.random() * 8;
  const rotation = Math.random() * 360;
  return {
    '--confetti-color': color,
    left: `${left}%`,
    width: `${size}px`,
    height: `${size * 0.4}px`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    transform: `rotate(${rotation}deg)`
  };
}

// ── Compute tooltip style ──
const isMobileViewport = () => window.innerWidth <= 640;

// Override tooltip body text for certain steps based on context
const tooltipBodyOverride = computed(() => {
  if (
    store.currentStep?.id === 'user-timeline-intro' &&
    !demoStore.isDemoMode
  ) {
    return "AI wrote this email based on the customer's browsing and purchase history. Click to preview the full email.";
  }
  if (store.currentStep?.id === 'customers-table') {
    if (demoStore.isDemoMode) {
      return 'Click any customer to see their full timeline.';
    }
    if (store.sandboxCustomer?.name) {
      return `Click ${store.sandboxCustomer.name} to see their full timeline.`;
    }
  }
  return '';
});

const tooltipStyle = computed(() => {
  // ── MOBILE: always pin tooltip to bottom of screen ──
  if (isMobileViewport()) {
    // Center modals / showcases stay centered on mobile
    if (
      !spotlightRect.value ||
      store.currentStep?.placement === 'center' ||
      store.currentStep?.showcase
    ) {
      return {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }
    // All other steps: pin to bottom
    return {
      position: 'fixed' as const,
      bottom: '8px',
      left: '8px',
      right: '8px',
      top: 'auto',
      transform: 'none',
      width: 'auto'
    };
  }

  // ── DESKTOP ──
  // Templates page: keep tooltip in the free bottom-right area
  if (store.currentStep?.id === 'templates-grid') {
    return {
      position: 'fixed' as const,
      bottom: '24px',
      right: '24px',
      top: 'auto',
      left: 'auto',
      transform: 'none'
    };
  }

  // Forms return step: dock below the navbar instead of anchoring above the
  // grid, which can push the card off-screen on shorter desktop viewports.
  if (store.currentStep?.id === 'forms-grid') {
    return {
      position: 'fixed' as const,
      bottom: '24px',
      left: 'calc(50% + 56px)',
      right: 'auto',
      top: 'auto',
      transform: 'translateX(-50%)',
      width: '400px',
      maxWidth: 'calc(100vw - 180px)'
    };
  }

  // Compact clickThrough: pin just above the spotlight target, centered
  if (store.currentStep?.compact && spotlightRect.value) {
    const rect = spotlightRect.value;
    const GAP = 20;
    return {
      position: 'fixed' as const,
      top: Math.max(4, rect.top - 48 - GAP) + 'px',
      left: rect.left + rect.width / 2 + 'px',
      transform: 'translateX(-50%)'
    };
  }

  if (!spotlightRect.value || store.currentStep?.placement === 'center') {
    // For clickThrough steps, pin to bottom-right so the content is fully visible
    if (store.currentStep?.clickThrough) {
      return {
        position: 'fixed' as const,
        bottom: '24px',
        right: '24px',
        top: 'auto',
        left: 'auto',
        transform: 'none'
      };
    }
    return {
      position: 'fixed' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  const rect = spotlightRect.value;
  const placement = store.currentStep?.placement || 'bottom';
  const GAP = 16;
  const TOOLTIP_WIDTH = 440;
  const TOOLTIP_HEIGHT = store.currentStep?.compact ? 72 : 320;
  const VIEWPORT_MARGIN = 12;

  let top = 0;
  let left = 0;
  let finalPlacement = placement;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      // If tooltip would overflow the bottom, flip to top
      if (top + TOOLTIP_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
        const topCandidate = rect.top - GAP - TOOLTIP_HEIGHT;
        if (topCandidate >= VIEWPORT_MARGIN) {
          top = rect.top - GAP;
          finalPlacement = 'top';
        }
      }
      break;
    case 'top':
      top = rect.top - GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      // If tooltip would overflow the top, flip to bottom
      if (top - TOOLTIP_HEIGHT < VIEWPORT_MARGIN) {
        const bottomCandidate = rect.bottom + GAP;
        if (
          bottomCandidate + TOOLTIP_HEIGHT <=
          window.innerHeight - VIEWPORT_MARGIN
        ) {
          top = bottomCandidate;
          finalPlacement = 'bottom';
        }
      }
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - GAP - TOOLTIP_WIDTH;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + GAP;
      break;
  }

  // Clamp to viewport
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
  );

  if (finalPlacement === 'top') {
    top = Math.max(
      TOOLTIP_HEIGHT + VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - VIEWPORT_MARGIN)
    );
  } else if (finalPlacement === 'left' || finalPlacement === 'right') {
    top = Math.max(
      TOOLTIP_HEIGHT / 2 + VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - TOOLTIP_HEIGHT / 2 - VIEWPORT_MARGIN)
    );
    // Pin vertical position only for the insight step where the target resizes
    // (typewriter effect changes height). Other left/right steps should track normally.
    if (store.currentStep?.id === 'insight-updated') {
      if (pinnedTooltipTop.value !== null) {
        top = pinnedTooltipTop.value;
      } else {
        const pinVal = top;
        nextTick(() => {
          pinnedTooltipTop.value = pinVal;
        });
      }
    }
  } else {
    // bottom placement — clamp so the card doesn't overflow viewport bottom
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - TOOLTIP_HEIGHT - VIEWPORT_MARGIN)
    );
  }

  // Shrink tooltip width to fit when placed right and it would overflow
  let width: string | undefined;
  if (finalPlacement === 'right') {
    const availableWidth = window.innerWidth - (rect.right + GAP) - 12;
    if (availableWidth < TOOLTIP_WIDTH) {
      width = Math.max(260, availableWidth) + 'px';
      left = rect.right + GAP;
    }
  }

  const transform =
    finalPlacement === 'top'
      ? 'translateY(-100%)'
      : finalPlacement === 'left' || finalPlacement === 'right'
      ? 'translateY(-50%)'
      : '';

  return {
    position: 'fixed' as const,
    top: top + 'px',
    left: left + 'px',
    transform,
    ...(width ? { width, minWidth: '260px' } : {})
  };
});

const pulseStyle = computed(() => {
  if (!spotlightRect.value) return {};
  const rect = spotlightRect.value;
  return {
    position: 'fixed' as const,
    top: rect.top - 4 + 'px',
    left: rect.left - 4 + 'px',
    width: rect.width + 8 + 'px',
    height: rect.height + 8 + 'px'
  };
});

// ── Listen for clicks on the real page during clickThrough steps ──
function handleDocumentClick(e: MouseEvent) {
  if (!store.isActive || !store.currentStep?.clickThrough) return;

  // Don't capture clicks on the tooltip or progress bar
  const target = e.target as HTMLElement;
  if (
    target.closest('.walkthrough-tooltip') ||
    target.closest('.walkthrough-progress-bar')
  )
    return;

  // Mark as transitioning so off-track detection ignores route change
  isTransitioning.value = true;

  // Reset transition guard if user clicks but does not navigate
  setTimeout(() => {
    if (store.currentStep?.clickThrough && !route.path.startsWith('/user/')) {
      isTransitioning.value = false;
    }
  }, 3000);
}

// ── Find and highlight target element (with retries) ──
async function updateSpotlight() {
  const step = store.currentStep;
  if (!step?.target) {
    stopSpotlightTracking();
    spotlightRect.value = null;
    return;
  }

  // Only wait for delay on page-transition steps (no beforeShow = data isn't pre-loaded)
  if (step.delay && !step.beforeShow) {
    await new Promise((resolve) => setTimeout(resolve, step.delay));
  }

  await nextTick();

  // Try to find the element immediately, retry only if not found
  let el: Element | null = null;
  let usingFallback = false;
  for (let attempt = 0; attempt < 10; attempt++) {
    if (step.id === 'session-summary') {
      // Find the newest walkthrough session by data-session-id.
      const newSessionId = getNewestWalkthroughSession()?.sessionId;
      if (newSessionId) {
        el = document.querySelector(
          `.session-summary-item[data-session-id="${newSessionId}"]`
        );
      }
      // Prefer skeleton over old sessions (skeleton is the correct target
      // during AI loading before the real new session exists in the DOM)
      if (!el) {
        el = document.querySelector('.session-skeleton-item');
      }
    } else if (step.id === 'user-timeline-intro' && !demoStore.isDemoMode) {
      // Real tenant: prefer sent AI email over scheduled
      el =
        document.querySelector(
          '.timeline-item.email-item.imessage-email-sent'
        ) || document.querySelector(step.target);
    } else if (step.id === 'customers-table' && demoStore.isDemoMode) {
      // Demo tenants don't have a sandbox row — highlight the whole table so all rows look clickable
      el = document.querySelector('.customers-table-wrapper');
    } else if (step.id === 'scheduled-messages') {
      // Target the LAST scheduled wrapper — the AI rewrite animation
      // typically happens on a lower/newer scheduled email
      const all = document.querySelectorAll('.scheduled-wrapper');
      el = all.length > 0 ? all[all.length - 1] : null;
    } else {
      el = document.querySelector(step.target);
    }
    if (el && (el as HTMLElement).offsetParent !== null) {
      const testRect = el.getBoundingClientRect();
      if (testRect.width > 0 && testRect.height > 0) break;
    }
    el = null;
    // Only wait on retries, not the first attempt
    if (attempt === 0) {
      await nextTick();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
  }

  // If primary target not found, try fallback (e.g. skeleton loader)
  if (!el && step.fallbackTarget) {
    const fb = document.querySelector(step.fallbackTarget);
    if (fb && (fb as HTMLElement).offsetParent !== null) {
      const fbRect = fb.getBoundingClientRect();
      if (fbRect.width > 0 && fbRect.height > 0) {
        el = fb;
        usingFallback = true;
      }
    }
  }

  if (el) {
    cleanupInteractive();

    // Scroll element into center of its scroll container
    const scrollParent = el.closest('.timeline-content') as HTMLElement | null;
    if (scrollParent) {
      // Add top padding so items near the start can scroll to center
      const pH = scrollParent.clientHeight;
      if (
        !scrollParent.style.paddingTop ||
        parseInt(scrollParent.style.paddingTop) < pH / 3
      ) {
        scrollParent.style.paddingTop = pH / 3 + 'px';
      }
      // Manually calculate scroll position to center element in container
      const containerRect = scrollParent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const elCenterInContainer =
        elRect.top -
        containerRect.top +
        scrollParent.scrollTop +
        elRect.height / 2;
      const targetScrollTop = elCenterInContainer - containerRect.height / 2;
      scrollParent.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
      // Wait for smooth scroll to settle
      await new Promise<void>((resolve) => {
        let prev = scrollParent.scrollTop;
        let settled = 0;
        const check = () => {
          const cur = scrollParent.scrollTop;
          if (Math.abs(cur - prev) < 1) settled++;
          else settled = 0;
          prev = cur;
          if (settled < 5) requestAnimationFrame(check);
          else resolve();
        };
        requestAnimationFrame(check);
      });
    }

    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      spotlightRect.value = null;
      return;
    }
    spotlightRect.value = rect;

    // Page-level scroll if needed (only for non-timeline targets)
    const topThreshold = isMobileViewport() ? 56 + 30 : 80; // navbar + progress bar on mobile
    const bottomThreshold = isMobileViewport()
      ? window.innerHeight - Math.min(window.innerHeight * 0.4, 240)
      : window.innerHeight - 80;
    if (
      !scrollParent &&
      (rect.top < topThreshold || rect.bottom > bottomThreshold)
    ) {
      // On mobile, scrollIntoView scrolls .app-container (overflow-y: auto)
      // Use 'start' on mobile so the element appears above the bottom tooltip
      // Add scroll-margin so the element doesn't sit flush at the top
      if (isMobileViewport()) {
        (el as HTMLElement).style.scrollMarginTop = '120px';
      }
      el.scrollIntoView({
        behavior: 'smooth',
        block: isMobileViewport() ? 'start' : 'center'
      });
      // Wait for scroll to settle before reading the rect again
      await new Promise((resolve) => setTimeout(resolve, 400));
      (el as HTMLElement).style.scrollMarginTop = '';
      const updatedRect = el.getBoundingClientRect();
      if (updatedRect.width > 0 && updatedRect.height > 0) {
        spotlightRect.value = updatedRect;
      }
    }

    startSpotlightTracking(el);

    // If using fallback, watch for the real target to appear and switch
    if (usingFallback && step.target) {
      stopFallbackWatch();
      const targetSel = step.target;
      fallbackMutationObserver = new MutationObserver(() => {
        const real = document.querySelector(targetSel);
        if (real && (real as HTMLElement).offsetParent !== null) {
          const r = real.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            stopFallbackWatch();
            // Switch spotlight to real element
            spotlightRect.value = r;
            startSpotlightTracking(real);
          }
        }
      });
      fallbackMutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    if (step.interactive) {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.add('walkthrough-interactive-target');
      htmlEl.style.position = 'relative';
      htmlEl.style.zIndex = '10001';
      interactiveEl = htmlEl;
    }
  } else {
    stopSpotlightTracking();
    spotlightRect.value = null;
  }
}

// ── Route navigation for steps ──
async function navigateIfNeeded() {
  const step = store.currentStep;
  if (!step?.route) return;

  // If the step route requires a userID but one hasn't been generated yet, wait
  if (
    step.route.includes(':userID') ||
    (step.route.includes('//') && !store.targetUserId)
  ) {
    if (!store.targetUserId) return; // handled by the generating overlay
  }

  const targetPath = step.route;

  // If already on a matching page pattern, don't re-navigate
  // e.g., already on /user/SOME_ID/PROJECT matches /user/:userID/:projectID
  if (route.path === targetPath) return;

  // Check if we're already on the right type of page (e.g., any user page)
  const targetPrefix = targetPath.split('/').slice(0, 2).join('/');
  const currentPrefix = route.path.split('/').slice(0, 2).join('/');
  if (targetPrefix === currentPrefix && targetPrefix.length > 1) {
    // Already on the right kind of page — no need to wait, updateSpotlight handles it
    return;
  }

  await router.push(targetPath);
  // Wait for route transition + rendering
  await new Promise((resolve) => setTimeout(resolve, step.delay || 500));
}

// ── Execute beforeShow actions ──
async function executeBeforeShow() {
  const step = store.currentStep;
  if (!step?.beforeShow) return;

  if (step.beforeShow === 'open-simulate') {
    // Programmatically open the simulate panel
    await new Promise((resolve) => setTimeout(resolve, 200));
    const simBtn = document.querySelector(
      '.desktop-only .sim-trigger-btn'
    ) as HTMLElement;
    if (simBtn) {
      // Only click if panel isn't already open
      const popup = document.querySelector('.sim-popup');
      if (!popup) simBtn.click();
    }
    // Wait for panel to open and render
    await new Promise((resolve) => setTimeout(resolve, 400));
  }

  if (step.beforeShow === 'refresh-timeline') {
    // On mobile, ensure we're on the Timeline tab
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'timeline' }
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Check if we already have cached AI data (e.g. going back to this step)
    if (store.aiComplete && cachedSummaries.value) {
      // Restore walkthrough state on the (possibly remounted) timeline component
      window.dispatchEvent(
        new CustomEvent('walkthrough-restore-session-state', {
          detail: { preSessionIds: cachedPreSessionIds.value || [] }
        })
      );

      // Ensure the new session is expanded (it may have been collapsed
      // if the timeline re-rendered while on a different step)
      const newSession = getNewestWalkthroughSession(cachedSummaries.value);

      // Expand the new session in the timeline data
      window.dispatchEvent(
        new CustomEvent('walkthrough-expand-session', {
          detail: { sessionId: newSession?.sessionId }
        })
      );

      // Wait for DOM to settle after expansion
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Replay session typewriter animation — pass cached summary so it doesn't
      // have to search for it (component may have remounted)
      if (newSession?.summary) {
        window.dispatchEvent(
          new CustomEvent('walkthrough-session-typewriter', {
            detail: {
              summary: newSession.summary,
              sessionId: newSession.sessionId
            }
          })
        );
      } else {
        window.dispatchEvent(new CustomEvent('walkthrough-session-typewriter'));
      }

      // Wait for typewriter to start and DOM to render
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Now spotlight and show tooltip
      await updateSpotlight();
      tooltipReady.value = true;
      return;
    }

    // Page reload: AI already completed but cached data is gone.
    // Session summaries are already in Firestore and will be loaded by
    // the normal loadTimelineData() call — no need to re-stream or show skeleton.
    if (store.aiComplete && !cachedSummaries.value) {
      // Wait for the timeline to finish loading customer data
      await new Promise((resolve) => setTimeout(resolve, 600));
      await updateSpotlight();
      tooltipReady.value = true;
      return;
    }

    // Show loading skeleton in the timeline
    window.dispatchEvent(new CustomEvent('walkthrough-snapshot-messages'));
    window.dispatchEvent(
      new CustomEvent('walkthrough-session-loading', {
        detail: { actionCount: lastSimDoneCount.value }
      })
    );

    // Wait for skeleton to render, then spotlight it
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 300));
    await updateSpotlight();
    // Skeleton is now spotlighted — show the tooltip
    tooltipReady.value = true;

    // Refetch customer early so simulate actions appear in the skeleton's events list
    // (runs in parallel with the AI stream below).
    const earlyRefreshPromise = requestWalkthroughRefresh();

    // Fire the combined walkthrough AI SSE endpoint
    const projectId = store.projectId || demoStore.demoTenantId;
    const customerId = store.targetUserId;
    if (!projectId || !customerId) {
      console.error('[walkthrough-ai] Missing projectId or customerId');
      window.dispatchEvent(new CustomEvent('walkthrough-session-ready'));
      return;
    }

    walkthroughAILoading.value = true;
    store.setAiComplete(false);

    try {
      await streamWalkthroughAI(projectId, customerId, {
        onSummary: (summaries) => {
          console.log(
            '[walkthrough-ai] Session summaries arrived:',
            summaries.length
          );
          cachedSummaries.value = summaries;
          // Find the new session's summary and start the typewriter immediately
          // on the skeleton (before AI stream fully completes)
          const newSession = getNewestWalkthroughSession(summaries);
          if (newSession?.summary) {
            window.dispatchEvent(
              new CustomEvent('walkthrough-session-typewriter', {
                detail: {
                  summary: newSession.summary,
                  sessionId: newSession.sessionId
                }
              })
            );
          }
        },
        onInsight: (previousInsight, newInsight) => {
          console.log(
            '[walkthrough-ai] Insight arrived, prevLen:',
            previousInsight?.length,
            'newLen:',
            newInsight?.length
          );
          cachedPreviousInsight.value = previousInsight;
          cachedNewInsight.value = newInsight;
        },
        onMessage: (messages) => {
          console.log('[walkthrough-ai] Messages arrived:', messages.length);
          cachedMessages.value = messages;
        },
        onError: (task, error) => {
          console.error(`[walkthrough-ai] Error in ${task}:`, error);
        },
        onDone: () => {
          console.log('[walkthrough-ai] All tasks complete');
        }
      });
    } catch (err) {
      console.error('[walkthrough-ai] Stream failed:', err);
    }

    walkthroughAILoading.value = false;
    store.setAiComplete(true);

    // Wait for the early refresh request to actually complete.
    await earlyRefreshPromise;

    // Refresh again to pick up AI-generated messages/summaries that were created
    // during the stream, and do not hide the skeleton until that request has
    // actually completed.
    await requestWalkthroughRefresh();

    // Hide skeleton (and build real session items in the same tick)
    window.dispatchEvent(new CustomEvent('walkthrough-session-ready'));
    // Wait for Vue to render the real session element (skeleton removal is instant)
    await nextTick();
    await nextTick();

    // Re-spotlight the real new session element (skeleton is now gone)
    await updateSpotlight();

    // Expand the new session explicitly (in case it rendered collapsed)
    window.dispatchEvent(
      new CustomEvent('walkthrough-expand-session', {
        detail: {
          sessionId: getNewestWalkthroughSession()?.sessionId
        }
      })
    );
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 100));
    // Refresh spotlight rect once more after expand
    refreshSpotlightRect();

    // Typewriter was already started during the stream (in onSummary callback)
    // No need to re-dispatch here — the animation continues seamlessly on the real session
  }

  if (step.beforeShow === 'walkthrough-messages') {
    // On mobile, ensure we're on the Timeline tab (coming back from insight/profile)
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'timeline' }
        })
      );
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Scroll to and spotlight the email FIRST, before any animation starts.
    // This prevents glitchy scroll during the typewriter.
    await updateSpotlight();
    tooltipReady.value = true;
    // Small pause so the spotlight settles before animation begins
    await new Promise((resolve) => setTimeout(resolve, 250));

    // Now start the typewriter animation
    if (cachedMessages.value && cachedMessages.value.length > 0) {
      console.log(
        '[walkthrough] Animating messages from cached AI data:',
        cachedMessages.value.length
      );
      window.dispatchEvent(
        new CustomEvent('walkthrough-animate-messages', {
          detail: { messages: cachedMessages.value }
        })
      );
    } else {
      console.log(
        '[walkthrough] Messages not ready yet — starting pending typewriter'
      );
      // Start the delete animation immediately using snapshotted content.
      // After deletion finishes the UI shows an "AI thinking" state.
      window.dispatchEvent(new CustomEvent('walkthrough-pending-typewriter'));

      // Poll until cachedMessages arrives — fire-and-forget (non-blocking)
      // so we don't hold up the tooltip or spotlight.
      // Store in component-level var so it can be cleared on step change.
      if (messagePollTimer) clearInterval(messagePollTimer);
      messagePollTimer = setInterval(() => {
        if (cachedMessages.value && cachedMessages.value.length > 0) {
          if (messagePollTimer) clearInterval(messagePollTimer);
          messagePollTimer = null;
          console.log(
            '[walkthrough] Messages arrived during pending typewriter:',
            cachedMessages.value.length
          );
          window.dispatchEvent(
            new CustomEvent('walkthrough-animate-messages', {
              detail: { messages: cachedMessages.value }
            })
          );
        }
      }, 200);
      // Safety: stop polling after 30s
      setTimeout(() => {
        if (messagePollTimer) {
          clearInterval(messagePollTimer);
          messagePollTimer = null;
        }
      }, 30000);
    }
    // Skip the normal updateSpotlight + tooltipReady below — already done above
    return;
  }

  if (step.beforeShow === 'walkthrough-insight') {
    // On mobile, the insight section lives in the Profile tab (UserInfoComponent)
    // which is hidden when mobileActiveTab === 'timeline'. Switch tabs first.
    if (isMobileViewport()) {
      window.dispatchEvent(
        new CustomEvent('walkthrough-switch-tab', {
          detail: { tab: 'profile' }
        })
      );
      // Wait for Vue to conditionally render UserInfoComponent
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    // Use cached insight data from the combined AI call
    if (
      cachedPreviousInsight.value !== null &&
      cachedNewInsight.value !== null
    ) {
      console.log('[walkthrough] Animating insight from cached AI data');
      window.dispatchEvent(
        new CustomEvent('walkthrough-animate-insight', {
          detail: {
            previousInsight: cachedPreviousInsight.value,
            newInsight: cachedNewInsight.value
          }
        })
      );
    } else {
      console.warn('[walkthrough] No cached insight data available');
      window.dispatchEvent(new CustomEvent('walkthrough-refresh'));
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  if (step.beforeShow === 'generate-walkthrough-forms') {
    // If forms have already been picked by another team member, skip the picker
    // and go straight to the forms grid. The second user doesn't need to re-pick.
    if (store.walkthroughFormsPicked) {
      console.log(
        '[walkthrough] Forms already picked by another user — skipping picker'
      );
      store.next();
      return;
    }

    // Await the prefetch that was kicked off earlier (templates-grid step).
    // If it hasn't started yet for some reason, start it now.
    await prefetchWalkthroughForms();

    // Re-check after prefetch — the backend may have reported forms as already picked
    if (store.walkthroughFormsPicked) {
      console.log('[walkthrough] Forms already picked — skipping picker');
      store.next();
      return;
    }
  }

  if (step.beforeShow === 'scroll-to-email') {
    // Refresh customer data so the async-generated email HTML is picked up
    // (email content is generated in the background during sandbox creation)
    window.dispatchEvent(new CustomEvent('walkthrough-refresh'));
    await new Promise((resolve) => setTimeout(resolve, 600));

    // Ensure the Email filter is active so email items are visible
    const emailFilterBtn = document.querySelector(
      '.filter-btn:not(.active)'
    ) as HTMLElement;
    // Find the Email filter specifically
    const allFilterBtns = document.querySelectorAll('.filter-btn');
    for (const btn of allFilterBtns) {
      if (
        btn.textContent?.includes('Email') &&
        !btn.classList.contains('active')
      ) {
        (btn as HTMLElement).click();
        await new Promise((resolve) => setTimeout(resolve, 300));
        break;
      }
    }

    // Now scroll the timeline container to show the right email
    // For real tenants: prefer sent emails (the AI-generated past email)
    // For demo: prefer scheduled emails
    await new Promise((resolve) => setTimeout(resolve, 200));
    let emailEl: HTMLElement | null = null;
    // Prefer emails with generated content (has thumbnail), then scheduled, then any
    // Target .item-content inside so spotlight wraps just the visual card
    emailEl = document.querySelector(
      '.timeline-item.email-item.email-has-content .item-content'
    ) as HTMLElement;
    if (!emailEl) {
      if (!demoStore.isDemoMode) {
        emailEl = (document.querySelector(
          '.timeline-item.email-item.imessage-email-sent .item-content'
        ) ||
          document.querySelector(
            '.timeline-item.email-item .item-content'
          )) as HTMLElement;
      } else {
        emailEl = (document.querySelector(
          '.timeline-item.email-item.scheduled-item .item-content'
        ) ||
          document.querySelector(
            '.timeline-item.email-item .item-content'
          )) as HTMLElement;
      }
    }
    const scrollContainer = document.querySelector(
      '.timeline-content'
    ) as HTMLElement;
    if (emailEl && scrollContainer) {
      // Add top + bottom padding so items anywhere can scroll to center
      const containerH = scrollContainer.clientHeight;
      scrollContainer.style.paddingTop = containerH / 3 + 'px';
      scrollContainer.style.paddingBottom = containerH / 2 + 'px';
      // Manually center the element within the timeline container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elRect = emailEl.getBoundingClientRect();
      const elCenterInContainer =
        elRect.top -
        containerRect.top +
        scrollContainer.scrollTop +
        elRect.height / 2;
      const targetScrollTop = elCenterInContainer - containerRect.height / 2;
      scrollContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
      // Wait for smooth scroll to settle
      await new Promise<void>((resolve) => {
        let prev = scrollContainer.scrollTop;
        let settled = 0;
        const check = () => {
          const cur = scrollContainer.scrollTop;
          if (Math.abs(cur - prev) < 1) settled++;
          else settled = 0;
          prev = cur;
          if (settled < 5) requestAnimationFrame(check);
          else resolve();
        };
        requestAnimationFrame(check);
      });
    }
  }
}

// ── Close simulate panel if it's open ──
function closeSimulatePanel() {
  const popup = document.querySelector('.sim-popup');
  if (popup) {
    const simBtn = document.querySelector(
      '.desktop-only .sim-trigger-btn'
    ) as HTMLElement;
    if (simBtn) simBtn.click(); // toggle it closed
  }
}

// ── Handle next step (navigate + spotlight) ──
async function handleNext() {
  // If this is the last step, complete & celebrate
  if (store.currentStepIndex >= store.totalSteps - 1) {
    // Clear spotlight before showing celebration so the last target
    // doesn't remain highlighted behind the CTA overlay
    stopSpotlightTracking();
    cleanupInteractive();
    spotlightRect.value = null;
    store.complete();
    showCelebration.value = true;
    return;
  }

  // Forms picker step: open the storefront picker instead of advancing
  if (store.currentStep?.id === 'forms-pick') {
    openWalkthroughPicker();
    return;
  }

  // Reset filters back to all-on when leaving the filter step
  if (store.currentStep?.id === 'user-timeline-filters') {
    cleanupInteractive();
    document.querySelectorAll('.filter-toggles .filter-btn').forEach((btn) => {
      if (!btn.classList.contains('active')) {
        (btn as HTMLElement).click();
      }
    });
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // Clean up interactive element boost for any step
  cleanupInteractive();

  // Close simulate panel before leaving the step
  if (store.currentStep?.beforeShow === 'open-simulate') {
    closeSimulatePanel();
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  // Clear old spotlight immediately so it doesn't linger during page transition
  spotlightRect.value = null;

  // store.next() mutates currentStepIndex, which triggers the watcher
  // that calls navigateIfNeeded → executeBeforeShow → updateSpotlight.
  // Do NOT call those here or they will run twice.
  store.next();
}

function handleOverlayClick() {
  // Don't advance on overlay click for showcase, clickThrough, or interactive steps
  if (
    store.currentStep?.showcase ||
    store.currentStep?.clickThrough ||
    store.currentStep?.interactive
  )
    return;
  handleNext();
}

function closeCelebration() {
  showCelebration.value = false;
  stopCelebrationAnimation();
  window.dispatchEvent(new CustomEvent('walkthrough-clear-message-changes'));
  // Clear cached walkthrough AI data
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  store.setAiComplete(false);
  // Reset celebration project state
  projectIsActive.value = null;
  activatingProject.value = false;
  activationSuccess.value = false;
  celebrationLoading.value = false;
  // Navigate to dashboard
  if (store.projectId) {
    router.push('/dashboard/' + store.projectId);
  }
}

// ── Fetch project active status when celebration shows (non-demo) ──
async function fetchProjectActiveStatus() {
  if (demoStore.isDemoMode) return;
  const projectId = store.projectId;
  if (!projectId) return;

  celebrationLoading.value = true;
  try {
    const projectData = await getProject(projectId, {
      includeCustomers: false
    });
    projectIsActive.value =
      (projectData as Record<string, unknown>).isActive === true;
  } catch (err) {
    console.error('[walkthrough] Failed to fetch project status:', err);
    projectIsActive.value = false;
  } finally {
    celebrationLoading.value = false;
  }
}

async function activateProject() {
  const projectId = store.projectId;
  if (!projectId || activatingProject.value) return;

  activatingProject.value = true;
  try {
    const result = await setActiveStatus(projectId, true);
    if (result.success) {
      projectIsActive.value = true;
      activationSuccess.value = true;
    }
  } catch (err) {
    console.error('[walkthrough] Failed to activate project:', err);
  } finally {
    activatingProject.value = false;
  }
}

function replayTour() {
  showCelebration.value = false;
  // Clear cached walkthrough AI data for fresh replay
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  store.setAiComplete(false);
  if (demoStore.demoTenantId) {
    store.restart(demoStore.demoTenantId, store.targetUserId || undefined);
  }
}

function goToShopifyInstall() {
  showCelebration.value = false;
  router.push('/shopify');
}

// ── Keyboard navigation ──
function handleKeydown(e: KeyboardEvent) {
  if (showCelebration.value) {
    if (e.key === 'Escape' || e.key === 'Enter') closeCelebration();
    return;
  }
  if (!store.isActive) return;

  // If a page modal is blocking the walkthrough, arrow-right / Enter / Escape closes it
  if (modalOpen.value) {
    if (
      e.key === 'ArrowRight' ||
      e.key === 'Enter' ||
      e.key === 'Escape' ||
      e.key === ' '
    ) {
      e.preventDefault();
      // Close any known modals
      demoStore.showOnboarding = false;
      // Click the modal's close / X button if one exists
      const closeBtn = document.querySelector(
        '.modal-overlay .close-btn, .modal-overlay .btn-secondary, .modal-overlay [aria-label="Close"]'
      ) as HTMLElement;
      if (closeBtn) closeBtn.click();
    }
    return;
  }

  switch (e.key) {
    case 'ArrowRight':
    case 'Enter':
    case ' ':
      e.preventDefault();
      // On the interactive simulate step, mirror the button behaviour:
      // sub-view with pending input → submit it; needs more actions → back to menu; done → advance
      if (store.currentStep?.showcase === 'simulate-interactive') {
        if (
          simRef.value?.view === 'search' &&
          simRef.value?.searchInput?.trim()
        ) {
          simRef.value.submitSearch();
          return;
        }
        if (simRef.value?.view === 'form' && simRef.value?.hasAnyAnswer) {
          simRef.value.submitForm();
          return;
        }
        if (simIsInSubView.value || simStepNeedsMore.value) {
          simBackToMenu();
          return;
        }
        // All actions done and on menu → fall through to handleNext
      }
      handleNext();
      break;
    case 'ArrowLeft':
      e.preventDefault();
      simIsInSubView.value ? simBackToMenu() : store.prev();
      break;
    case 'Escape':
      store.dismiss();
      break;
  }
}

// ── Fetch project status when celebration shows (non-demo) ──
watch(showCelebration, (visible) => {
  if (visible) {
    startCelebrationAnimation();
    if (!demoStore.isDemoMode) {
      fetchProjectActiveStatus();
    }
  } else {
    stopCelebrationAnimation();
  }
});

// ── Watch for step changes ──
watch(
  () => store.currentStepIndex,
  async () => {
    if (!store.isActive) return;
    // Immediately clear previous step's highlight and tracking
    stopSpotlightTracking();
    cleanupInteractive();
    disableScrollLock();
    spotlightRect.value = null;
    pinnedTooltipTop.value = null;
    stopShowcaseAnimation();
    stopLfCycle();
    // Kill any stale message poll from a previous step
    if (messagePollTimer) {
      clearInterval(messagePollTimer);
      messagePollTimer = null;
    }
    // Capture sim count BEFORE reset so session-loading event gets the real number
    lastSimDoneCount.value = simDoneCount.value;
    simRef.value?.reset();
    // Hide tooltip until data is loaded / spotlight is positioned.
    // Also hide during beforeShow so users can't click buttons before data is ready
    // (e.g. "Open picker →" before shopName is fetched).
    const step = store.currentStep;
    const stepIdx = store.currentStepIndex;
    const needsSpotlight = !!step?.target;
    if (needsSpotlight || step?.beforeShow) tooltipReady.value = false;
    await navigateIfNeeded();
    await executeBeforeShow();
    // Guard: if step changed during beforeShow (e.g. auto-skipped), bail
    if (store.currentStepIndex !== stepIdx) return;
    // Some beforeShow handlers (e.g. walkthrough-messages) return early after
    // handling their own spotlight + tooltip. If tooltipReady is already true,
    // skip the redundant updateSpotlight.
    if (!tooltipReady.value) {
      await updateSpotlight();
      tooltipReady.value = true;
    }
    // Lock scroll if this step requires it (e.g. session-summary)
    if (store.currentStep?.noScroll) {
      enableScrollLock();
    }
    // Prefetch walkthrough forms in the background once we reach the Results group
    // (several steps before forms-pick) so data is ready when the user gets there.
    if (
      store.currentStep?.group === 'Results' ||
      store.currentStep?.group === 'Templates'
    ) {
      prefetchWalkthroughForms();
    }
    // Start showcase animation if this step has one
    if (
      store.currentStep?.showcase === 'simulate' ||
      store.currentStep?.showcase === 'integrations' ||
      store.currentStep?.showcase === 'welcome' ||
      store.currentStep?.showcase === 'forms' ||
      store.currentStep?.showcase === 'forms-pick'
    ) {
      startShowcaseAnimation();
    }
  }
);

// ── Auto-dismiss if user navigates to a non-project route (logo, back button, etc.) ──
watch(
  () => route.path,
  (newPath) => {
    if (!store.isActive) return;
    // Non-project routes: landing, auth, projects list, shopify install, etc.
    const nonProjectPaths = [
      '/',
      '/auth',
      '/projects',
      '/shopify',
      '/terms',
      '/privacy'
    ];
    if (nonProjectPaths.includes(newPath)) {
      store.dismiss();
    }
  }
);

// ── Handle click-through route transitions (e.g. customer row click) ──
watch(
  () => route.path,
  async (newPath) => {
    if (!store.isActive) return;

    const step = store.currentStep;
    if (!step?.clickThrough || step.id !== 'customers-table') return;
    if (!newPath.startsWith('/user/')) return;
    if (isClickThroughAdvanceInProgress.value) return;

    isClickThroughAdvanceInProgress.value = true;
    isTransitioning.value = true;

    // Stop spotlight tracking immediately so the recovery timer doesn't
    // try to re-acquire .sandbox-row on a page where it no longer exists
    stopSpotlightTracking();
    spotlightRect.value = null;

    const match = newPath.match(/\/user\/([^/]+)\//);
    if (match && match[1]) {
      store.setTargetUser(match[1]);
    }

    // Wait for the user page to render before advancing
    await new Promise((resolve) => setTimeout(resolve, 800));
    await handleNext();

    isTransitioning.value = false;
    isClickThroughAdvanceInProgress.value = false;
  }
);

// ── Watch for window resize ──
let resizeTimeout: ReturnType<typeof setTimeout>;
function handleResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => updateSpotlight(), 150);
}

// ── Lifecycle ──
onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', handleResize);
  window.addEventListener('message', handlePickerMessage);
  window.addEventListener(
    'walkthrough-session-ids-captured',
    handleSessionIdsCaptured
  );
  document.addEventListener('click', handleDocumentClick, true);

  // Watch for page modals (.modal-overlay) appearing — hide walkthrough while open
  modalObserver = new MutationObserver(() => {
    const hasModal = !!document.querySelector(
      '.modal-overlay, .modal-backdrop, .idea-editor-overlay, .time-editor-overlay, .sms-editor-overlay, .text-email-editor-overlay'
    );
    modalOpen.value = hasModal;
  });
  modalObserver.observe(document.body, { childList: true, subtree: true });

  if (store.isActive) {
    // Re-fetch sandbox customer data if restored from localStorage (page refresh)
    store.hydrate().then(() => {
      navigateIfNeeded().then(async () => {
        await executeBeforeShow();
        await updateSpotlight();
        if (
          store.currentStep?.showcase === 'simulate' ||
          store.currentStep?.showcase === 'integrations' ||
          store.currentStep?.showcase === 'welcome' ||
          store.currentStep?.showcase === 'forms' ||
          store.currentStep?.showcase === 'forms-pick'
        )
          startShowcaseAnimation();
      });
    });
  }
});

// ── Dev helper: replay from simulate step without re-creating tenant ──
// Usage: open browser console → __wtReplay()
// Optionally: __wtReplay(8) to go to a specific step index
(window as any).__wtReplay = (stepIndex?: number) => {
  // Default to simulate-showcase (step 8)
  const target =
    stepIndex ?? store.steps.findIndex((s) => s.id === 'simulate-showcase');
  // Clear cached AI data so refresh-timeline re-streams
  store.setAiComplete(false);
  cachedSummaries.value = null;
  cachedPreviousInsight.value = null;
  cachedNewInsight.value = null;
  cachedMessages.value = null;
  cachedPreSessionIds.value = null;
  walkthroughAILoading.value = false;
  formsPrefetchPromise = null; // allow re-prefetch on replay
  // Clear timeline walkthrough state
  window.dispatchEvent(new CustomEvent('walkthrough-clear-message-changes'));
  // Stop any running spotlight / animation
  stopSpotlightTracking();
  cleanupInteractive();
  stopShowcaseAnimation();
  // Jump to step
  store.goToStep(target >= 0 ? target : 8);
  console.log(
    `[__wtReplay] Reset to step ${store.currentStepIndex} (${store.currentStep?.id})`
  );
};

// ── Dev helper: show the celebration / go-live screen ──
// Usage: open browser console → __wtCelebrate()
(window as any).__wtCelebrate = () => {
  showCelebration.value = true;
  console.log('[__wtCelebrate] Celebration screen opened');
};

// ── Dev helper: reset & regenerate walkthrough forms ──
// Usage: open browser console → __wtResetForms()
(window as any).__wtResetForms = async () => {
  const projectId = store.projectId;
  if (!projectId) {
    console.error('[__wtResetForms] No project ID found in route');
    return;
  }
  try {
    // Delete ALL forms for the tenant first
    const allForms = await getLeadForms(projectId);
    console.log(`[__wtResetForms] Deleting all ${allForms.length} forms…`);
    await Promise.all(allForms.map((f) => deleteLeadForm(projectId, f.id)));
    console.log(
      '[__wtResetForms] All forms deleted. Regenerating walkthrough forms…'
    );
    const result = await resetWalkthroughForms(projectId);
    store.walkthroughFormsGenerated = true;
    console.log(
      `[__wtResetForms] Done! ${result.forms?.length || 0} forms regenerated.`,
      result
    );
  } catch (err) {
    console.error('[__wtResetForms] Failed:', err);
  }
};

onUnmounted(() => {
  delete (window as any).__wtReplay;
  delete (window as any).__wtCelebrate;
  delete (window as any).__wtResetForms;
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('message', handlePickerMessage);
  window.removeEventListener(
    'walkthrough-session-ids-captured',
    handleSessionIdsCaptured
  );
  document.removeEventListener('click', handleDocumentClick, true);
  if (modalObserver) {
    modalObserver.disconnect();
    modalObserver = null;
  }
  document.body.classList.remove('walkthrough-active');
  stopShowcaseAnimation();
  stopLfCycle();
  stopSpotlightTracking();
  cleanupInteractive();
  disableScrollLock();
  simRef.value?.reset();
});

// Toggle body class so the app navbar can be z-boosted above the overlay
function syncWalkthroughBodyClass(active: boolean) {
  if (active) {
    document.body.classList.add('walkthrough-active');
  } else {
    document.body.classList.remove('walkthrough-active');
    disableScrollLock();
  }
}

// When walkthrough becomes active, init
watch(
  () => store.isActive,
  async (active) => {
    syncWalkthroughBodyClass(active);
    if (active) {
      await navigateIfNeeded();
      await executeBeforeShow();
      await updateSpotlight();
      if (
        store.currentStep?.showcase === 'simulate' ||
        store.currentStep?.showcase === 'integrations' ||
        store.currentStep?.showcase === 'welcome' ||
        store.currentStep?.showcase === 'forms'
      )
        startShowcaseAnimation();
    } else {
      stopShowcaseAnimation();
      stopLfCycle();
      simRef.value?.reset();
    }
  },
  { immediate: true }
);

// When targetUserId becomes available (sandbox user generated), resume navigation
watch(
  () => store.targetUserId,
  async (newId) => {
    if (!store.isActive || !newId) return;
    // If we were waiting for user generation, now we can proceed
    await navigateIfNeeded();
    await executeBeforeShow();
    await updateSpotlight();
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "walkthrough-fade" }, {
      default: _withCtx(() => [
        (_unref(store).isActive && _unref(store).currentStep && !awaitingUserGeneration.value)
          ? _withDirectives((_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["walkthrough-wrapper", {
          'click-through-active': _unref(store).currentStep?.clickThrough,
          'interactive-active': _unref(store).currentStep?.interactive,
          'offtrack-active': isOffTrack.value
        }])
            }, [
              _createElementVNode("div", {
                class: "walkthrough-overlay",
                onClick: _withModifiers(handleOverlayClick, ["self"])
              }, [
                (spotlightRect.value && !isOffTrack.value)
                  ? (_openBlock(), _createElementBlock("svg", _hoisted_1, [
                      _createElementVNode("defs", null, [
                        _createElementVNode("mask", _hoisted_2, [
                          _cache[8] || (_cache[8] = _createElementVNode("rect", {
                            width: "100%",
                            height: "100%",
                            fill: "white"
                          }, null, -1)),
                          _createElementVNode("rect", {
                            x: spotlightRect.value.x - 8,
                            y: spotlightRect.value.y - 8,
                            width: spotlightRect.value.width + 16,
                            height: spotlightRect.value.height + 16,
                            rx: "12",
                            ry: "12",
                            fill: "black"
                          }, null, 8, _hoisted_3)
                        ])
                      ]),
                      _cache[9] || (_cache[9] = _createElementVNode("rect", {
                        width: "100%",
                        height: "100%",
                        fill: "rgba(0,0,0,0.6)",
                        mask: "url(#walkthrough-mask)"
                      }, null, -1))
                    ]))
                  : _createCommentVNode("", true),
                (!spotlightRect.value && !_unref(store).currentStep?.clickThrough)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4))
                  : _createCommentVNode("", true),
                (spotlightRect.value && _unref(store).currentStep?.pulse)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 2,
                      class: "pulse-ring",
                      style: _normalizeStyle(pulseStyle.value)
                    }, null, 4))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_Transition, { name: "tooltip-pop" }, {
                default: _withCtx(() => [
                  _withDirectives((_openBlock(), _createElementBlock("div", {
                    key: _unref(store).currentStep?.id,
                    class: _normalizeClass(["walkthrough-tooltip", [
              'placement-' + (_unref(store).currentStep?.placement || 'center'),
              { 'is-center': !spotlightRect.value },
              { 'is-compact': _unref(store).currentStep?.compact },
              { 'is-showcase': _unref(store).currentStep?.showcase }
            ]]),
                    style: _normalizeStyle(tooltipStyle.value)
                  }, [
                    (_unref(store).currentStep?.showcase === 'simulate')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          _createElementVNode("div", _hoisted_6, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (i) => {
                              return _createElementVNode("div", {
                                key: i,
                                class: _normalizeClass(["sc-particle", `sc-p-${i}`])
                              }, null, 2)
                            }), 64))
                          ]),
                          _createElementVNode("div", {
                            class: _normalizeClass(["sc-hero", { 'sc-visible': showcaseStep.value >= 1 }])
                          }, [...(_cache[10] || (_cache[10] = [
                            _createElementVNode("div", { class: "sc-badge" }, [
                              _createElementVNode("span", { class: "sc-badge-dot" }),
                              _createTextVNode(" POWERED BY AI ")
                            ], -1),
                            _createElementVNode("h2", { class: "sc-hero-title" }, [
                              _createTextVNode(" See how "),
                              _createElementVNode("span", { class: "sc-gradient-text" }, "MyUser"),
                              _createTextVNode(" turns shoppers into revenue ")
                            ], -1),
                            _createElementVNode("p", { class: "sc-hero-sub" }, " Every customer action triggers intelligent, personalized outreach — completely on autopilot. ", -1)
                          ]))], 2),
                          _createElementVNode("div", _hoisted_7, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-stage", { 'sc-visible': showcaseStep.value >= 2 }])
                            }, [...(_cache[11] || (_cache[11] = [
                              _createElementVNode("div", { class: "sc-stage-num sc-num-blue" }, "1", -1),
                              _createElementVNode("div", { class: "sc-stage-card sc-card-blue" }, [
                                _createElementVNode("div", { class: "sc-stage-icon" }, "🛒"),
                                _createElementVNode("h3", { class: "sc-stage-title" }, "Customer Acts"),
                                _createElementVNode("p", { class: "sc-stage-desc" }, " Every action is tracked in real-time "),
                                _createElementVNode("ul", { class: "sc-action-list" }, [
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-act-dot sc-dot-bg-blue" }),
                                    _createTextVNode(" Browses products ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-act-dot sc-dot-bg-amber" }),
                                    _createTextVNode(" Adds items to cart ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-act-dot sc-dot-bg-violet" }),
                                    _createTextVNode(" Searches the store ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-act-dot sc-dot-bg-green" }),
                                    _createTextVNode(" Completes a purchase ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-act-dot sc-dot-bg-red" }),
                                    _createTextVNode(" Leaves without buying ")
                                  ])
                                ])
                              ], -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-connector", { 'sc-visible': showcaseStep.value >= 3 }])
                            }, [...(_cache[12] || (_cache[12] = [
                              _createElementVNode("span", { class: "sc-chevron" }, "›", -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-stage", { 'sc-visible': showcaseStep.value >= 3 }])
                            }, [...(_cache[13] || (_cache[13] = [
                              _createElementVNode("div", { class: "sc-stage-num sc-num-purple" }, "2", -1),
                              _createElementVNode("div", { class: "sc-stage-card sc-card-purple" }, [
                                _createElementVNode("div", { class: "sc-brain" }, [
                                  _createElementVNode("div", { class: "sc-brain-ring" }),
                                  _createElementVNode("div", { class: "sc-brain-ring sc-ring-2" }),
                                  _createElementVNode("span", { class: "sc-brain-emoji" }, "🧠")
                                ]),
                                _createElementVNode("h3", { class: "sc-stage-title" }, "AI Decides"),
                                _createElementVNode("p", { class: "sc-stage-desc" }, "Adapts in real-time"),
                                _createElementVNode("ul", { class: "sc-ai-list" }, [
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-ai-check" }, "✓"),
                                    _createTextVNode(" Generates personalized messages ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-ai-check" }, "✓"),
                                    _createTextVNode(" Adjusts the plan as things change ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("span", { class: "sc-ai-check" }, "✓"),
                                    _createTextVNode(" Responds to incoming texts ")
                                  ])
                                ])
                              ], -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-connector", { 'sc-visible': showcaseStep.value >= 4 }])
                            }, [...(_cache[14] || (_cache[14] = [
                              _createElementVNode("span", { class: "sc-chevron" }, "›", -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-stage sc-stage-msgs", { 'sc-visible': showcaseStep.value >= 4 }])
                            }, [
                              _cache[19] || (_cache[19] = _createElementVNode("div", { class: "sc-stage-num sc-num-green" }, "3", -1)),
                              _createElementVNode("div", _hoisted_8, [
                                _cache[17] || (_cache[17] = _createElementVNode("h3", { class: "sc-stage-title" }, "Messages Sent", -1)),
                                _cache[18] || (_cache[18] = _createElementVNode("p", { class: "sc-stage-desc" }, " Personalized & timed to convert ", -1)),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["sc-email-card", { 'sc-visible': showcaseStep.value >= 5 }])
                                }, [...(_cache[15] || (_cache[15] = [
                                  _createElementVNode("div", { class: "sc-email-topbar" }, [
                                    _createElementVNode("span", { class: "sc-email-icon" }, "✉️"),
                                    _createElementVNode("span", { class: "sc-email-label" }, "EMAIL")
                                  ], -1),
                                  _createElementVNode("div", { class: "sc-email-subject" }, " New arrivals that match your style ✨ ", -1),
                                  _createElementVNode("div", { class: "sc-email-preview" }, " Hi Sarah, we picked these just for you based on your recent browsing… ", -1),
                                  _createElementVNode("div", { class: "sc-email-cta" }, "Shop Now →", -1)
                                ]))], 2),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["sc-phone sc-phone-sms", { 'sc-visible': showcaseStep.value >= 6 }])
                                }, [...(_cache[16] || (_cache[16] = [
                                  _createElementVNode("div", { class: "sc-phone-header" }, [
                                    _createElementVNode("span", { class: "sc-phone-dot sc-dot-green" }),
                                    _createElementVNode("span", { class: "sc-phone-dot sc-dot-green" }),
                                    _createElementVNode("span", { class: "sc-phone-dot sc-dot-green" })
                                  ], -1),
                                  _createElementVNode("div", { class: "sc-phone-body" }, [
                                    _createElementVNode("div", { class: "sc-phone-label" }, [
                                      _createElementVNode("span", { class: "sc-phone-badge sc-badge-sms" }, " 💬 "),
                                      _createTextVNode(" SMS ")
                                    ]),
                                    _createElementVNode("div", { class: "sc-sms-bubble" }, " Hey! Your cart is waiting — 10% off today 🎉 "),
                                    _createElementVNode("div", { class: "sc-sms-meta" }, "Delivered · just now")
                                  ], -1)
                                ]))], 2)
                              ])
                            ], 2)
                          ]),
                          _createElementVNode("div", _hoisted_9, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-m-step", { 'sc-visible': showcaseStep.value >= 2 }])
                            }, [...(_cache[20] || (_cache[20] = [
                              _createElementVNode("div", { class: "sc-m-line" }, [
                                _createElementVNode("div", { class: "sc-m-dot sc-dot-blue" })
                              ], -1),
                              _createElementVNode("div", { class: "sc-m-card sc-m-blue" }, [
                                _createElementVNode("div", { class: "sc-m-top" }, [
                                  _createElementVNode("span", { class: "sc-m-num" }, "1"),
                                  _createElementVNode("span", { class: "sc-m-icon" }, "🛒"),
                                  _createElementVNode("span", { class: "sc-m-title" }, "Customer Acts")
                                ]),
                                _createElementVNode("div", { class: "sc-m-chips" }, [
                                  _createElementVNode("span", null, [
                                    _createElementVNode("span", {
                                      class: "sc-act-dot",
                                      style: {"background":"#3b82f6"}
                                    }),
                                    _createTextVNode(" Views product")
                                  ]),
                                  _createElementVNode("span", null, [
                                    _createElementVNode("span", {
                                      class: "sc-act-dot",
                                      style: {"background":"#f59e0b"}
                                    }),
                                    _createTextVNode(" Adds to cart")
                                  ]),
                                  _createElementVNode("span", null, [
                                    _createElementVNode("span", {
                                      class: "sc-act-dot",
                                      style: {"background":"#8b5cf6"}
                                    }),
                                    _createTextVNode(" Searches")
                                  ]),
                                  _createElementVNode("span", null, [
                                    _createElementVNode("span", {
                                      class: "sc-act-dot",
                                      style: {"background":"#10b981"}
                                    }),
                                    _createTextVNode(" Purchases")
                                  ]),
                                  _createElementVNode("span", null, [
                                    _createElementVNode("span", {
                                      class: "sc-act-dot",
                                      style: {"background":"#ef4444"}
                                    }),
                                    _createTextVNode(" Abandons")
                                  ])
                                ])
                              ], -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-m-step", { 'sc-visible': showcaseStep.value >= 3 }])
                            }, [...(_cache[21] || (_cache[21] = [
                              _createElementVNode("div", { class: "sc-m-line" }, [
                                _createElementVNode("div", { class: "sc-m-dot sc-dot-purple" })
                              ], -1),
                              _createElementVNode("div", { class: "sc-m-card sc-m-purple" }, [
                                _createElementVNode("div", { class: "sc-m-top" }, [
                                  _createElementVNode("span", { class: "sc-m-num" }, "2"),
                                  _createElementVNode("span", { class: "sc-m-icon" }, "🧠"),
                                  _createElementVNode("span", { class: "sc-m-title" }, "AI Decides")
                                ]),
                                _createElementVNode("p", { class: "sc-m-desc" }, " Picks the best template, writes copy, and optimizes send time ")
                              ], -1)
                            ]))], 2),
                            _createElementVNode("div", {
                              class: _normalizeClass(["sc-m-step", { 'sc-visible': showcaseStep.value >= 4 }])
                            }, [
                              _cache[25] || (_cache[25] = _createElementVNode("div", { class: "sc-m-line sc-m-line-last" }, [
                                _createElementVNode("div", { class: "sc-m-dot sc-dot-green" })
                              ], -1)),
                              _createElementVNode("div", _hoisted_10, [
                                _cache[24] || (_cache[24] = _createElementVNode("div", { class: "sc-m-top" }, [
                                  _createElementVNode("span", { class: "sc-m-num" }, "3"),
                                  _createElementVNode("span", { class: "sc-m-icon" }, "📨"),
                                  _createElementVNode("span", { class: "sc-m-title" }, "Messages Sent")
                                ], -1)),
                                _createElementVNode("div", _hoisted_11, [
                                  _createElementVNode("div", {
                                    class: _normalizeClass(["sc-m-bub sc-m-bub-email", { 'sc-visible': showcaseStep.value >= 5 }])
                                  }, [...(_cache[22] || (_cache[22] = [
                                    _createElementVNode("span", { class: "sc-m-bub-icon" }, "✉️", -1),
                                    _createElementVNode("div", { class: "sc-m-bub-body" }, [
                                      _createElementVNode("strong", null, "Email"),
                                      _createElementVNode("span", null, " New arrivals that match your style ✨ "),
                                      _createElementVNode("span", { class: "sc-m-cta-pill" }, "Shop Now →")
                                    ], -1)
                                  ]))], 2),
                                  _createElementVNode("div", {
                                    class: _normalizeClass(["sc-m-bub sc-m-bub-sms", { 'sc-visible': showcaseStep.value >= 6 }])
                                  }, [...(_cache[23] || (_cache[23] = [
                                    _createElementVNode("span", { class: "sc-m-bub-icon" }, "💬", -1),
                                    _createElementVNode("div", { class: "sc-m-bub-body" }, [
                                      _createElementVNode("strong", null, "SMS"),
                                      _createElementVNode("span", null, " Your cart is waiting — 10% off today 🎉 ")
                                    ], -1)
                                  ]))], 2)
                                ])
                              ])
                            ], 2)
                          ])
                        ]))
                      : (_unref(store).currentStep?.showcase === 'integrations')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["ig-hero", { 'sc-visible': showcaseStep.value >= 1 }])
                            }, [...(_cache[26] || (_cache[26] = [
                              _createElementVNode("h2", { class: "ig-title" }, [
                                _createTextVNode(" Emails & texts are "),
                                _createElementVNode("span", { class: "sc-gradient-text" }, "ready to go")
                              ], -1),
                              _createElementVNode("p", { class: "ig-subtitle" }, " We handle delivery for you automatically. You can optionally connect your own accounts for more control. ", -1)
                            ]))], 2),
                            _createElementVNode("div", _hoisted_13, [
                              _createElementVNode("div", {
                                class: _normalizeClass(["ig-service", { 'sc-visible': showcaseStep.value >= 2 }])
                              }, [...(_cache[27] || (_cache[27] = [
                                _createElementVNode("div", { class: "ig-service-logo" }, [
                                  _createElementVNode("img", {
                                    src: _imports_0,
                                    alt: "SendGrid",
                                    class: "ig-logo-img"
                                  })
                                ], -1),
                                _createElementVNode("div", { class: "ig-service-info" }, [
                                  _createElementVNode("h3", { class: "ig-service-name" }, "SendGrid"),
                                  _createElementVNode("p", { class: "ig-service-desc" }, " Send emails from your own domain instead of “via MyUser” ")
                                ], -1)
                              ]))], 2),
                              _createElementVNode("div", {
                                class: _normalizeClass(["ig-service", { 'sc-visible': showcaseStep.value >= 3 }])
                              }, [...(_cache[28] || (_cache[28] = [
                                _createElementVNode("div", { class: "ig-service-logo" }, [
                                  _createElementVNode("img", {
                                    src: _imports_1,
                                    alt: "Twilio",
                                    class: "ig-logo-img"
                                  })
                                ], -1),
                                _createElementVNode("div", { class: "ig-service-info" }, [
                                  _createElementVNode("h3", { class: "ig-service-name" }, "Twilio"),
                                  _createElementVNode("p", { class: "ig-service-desc" }, " Send texts from your own phone number instead of a shared one ")
                                ], -1)
                              ]))], 2)
                            ]),
                            _createElementVNode("div", {
                              class: _normalizeClass(["ig-note", { 'sc-visible': showcaseStep.value >= 4 }])
                            }, " Neither is required — everything works without them. ", 2)
                          ]))
                        : (_unref(store).currentStep?.showcase === 'forms')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                              _createElementVNode("div", {
                                class: _normalizeClass(["lf-hero", { 'sc-visible': showcaseStep.value >= 1 }])
                              }, [...(_cache[29] || (_cache[29] = [
                                _createElementVNode("div", { class: "lf-badge" }, [
                                  _createElementVNode("svg", {
                                    width: "12",
                                    height: "12",
                                    viewBox: "0 0 16 16",
                                    fill: "none"
                                  }, [
                                    _createElementVNode("path", {
                                      d: "M8 1l1.8 5.4H15l-4.2 3.1 1.6 5-4.4-3.2L3.6 14.5l1.6-5L1 6.4h5.2z",
                                      fill: "currentColor"
                                    })
                                  ]),
                                  _createTextVNode(" Lead Capture ")
                                ], -1),
                                _createElementVNode("h2", { class: "lf-title" }, [
                                  _createTextVNode(" Turn every visitor into a "),
                                  _createElementVNode("span", { class: "sc-gradient-text" }, "known customer")
                                ], -1),
                                _createElementVNode("p", { class: "lf-subtitle" }, " AI-designed forms appear at the perfect moment on your storefront — every signup auto-linked to browsing behavior. ", -1)
                              ]))], 2),
                              _createElementVNode("div", {
                                class: _normalizeClass(["lf-tabs", { 'sc-visible': showcaseStep.value >= 2 }])
                              }, [
                                _createElementVNode("button", {
                                  class: _normalizeClass(["lf-tab", { 'lf-tab-active': lfActiveType.value === 'popup' }]),
                                  onClick: _cache[0] || (_cache[0] = ($event: any) => (pickLfType('popup')))
                                }, [...(_cache[30] || (_cache[30] = [
                                  _createElementVNode("svg", {
                                    width: "15",
                                    height: "15",
                                    viewBox: "0 0 16 16",
                                    fill: "none"
                                  }, [
                                    _createElementVNode("rect", {
                                      x: "1",
                                      y: "2",
                                      width: "14",
                                      height: "12",
                                      rx: "2",
                                      stroke: "currentColor",
                                      "stroke-width": "1.3"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "4",
                                      y: "5",
                                      width: "8",
                                      height: "6",
                                      rx: "1.5",
                                      fill: "currentColor",
                                      opacity: "0.12"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "4",
                                      y: "5",
                                      width: "8",
                                      height: "6",
                                      rx: "1.5",
                                      stroke: "currentColor",
                                      "stroke-width": "1.1"
                                    })
                                  ], -1),
                                  _createTextVNode(" Popup ", -1)
                                ]))], 2),
                                _createElementVNode("button", {
                                  class: _normalizeClass(["lf-tab", { 'lf-tab-active': lfActiveType.value === 'slidein' }]),
                                  onClick: _cache[1] || (_cache[1] = ($event: any) => (pickLfType('slidein')))
                                }, [...(_cache[31] || (_cache[31] = [
                                  _createElementVNode("svg", {
                                    width: "15",
                                    height: "15",
                                    viewBox: "0 0 16 16",
                                    fill: "none"
                                  }, [
                                    _createElementVNode("rect", {
                                      x: "1",
                                      y: "2",
                                      width: "14",
                                      height: "12",
                                      rx: "2",
                                      stroke: "currentColor",
                                      "stroke-width": "1.3"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "8",
                                      y: "7",
                                      width: "6",
                                      height: "6",
                                      rx: "1.5",
                                      fill: "currentColor",
                                      opacity: "0.12"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "8",
                                      y: "7",
                                      width: "6",
                                      height: "6",
                                      rx: "1.5",
                                      stroke: "currentColor",
                                      "stroke-width": "1.1"
                                    })
                                  ], -1),
                                  _createTextVNode(" Slide-in ", -1)
                                ]))], 2),
                                _createElementVNode("button", {
                                  class: _normalizeClass(["lf-tab", { 'lf-tab-active': lfActiveType.value === 'banner' }]),
                                  onClick: _cache[2] || (_cache[2] = ($event: any) => (pickLfType('banner')))
                                }, [...(_cache[32] || (_cache[32] = [
                                  _createElementVNode("svg", {
                                    width: "15",
                                    height: "15",
                                    viewBox: "0 0 16 16",
                                    fill: "none"
                                  }, [
                                    _createElementVNode("rect", {
                                      x: "1",
                                      y: "2",
                                      width: "14",
                                      height: "12",
                                      rx: "2",
                                      stroke: "currentColor",
                                      "stroke-width": "1.3"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "2.5",
                                      y: "3.5",
                                      width: "11",
                                      height: "3",
                                      rx: "1",
                                      fill: "currentColor",
                                      opacity: "0.12"
                                    }),
                                    _createElementVNode("rect", {
                                      x: "2.5",
                                      y: "3.5",
                                      width: "11",
                                      height: "3",
                                      rx: "1",
                                      stroke: "currentColor",
                                      "stroke-width": "1.1"
                                    })
                                  ], -1),
                                  _createTextVNode(" Banner ", -1)
                                ]))], 2)
                              ], 2),
                              _createElementVNode("div", {
                                class: _normalizeClass(["lf-stage", { 'sc-visible': showcaseStep.value >= 2 }])
                              }, [
                                _createElementVNode("div", _hoisted_15, [
                                  _cache[40] || (_cache[40] = _createElementVNode("div", { class: "lf-browser-bar" }, [
                                    _createElementVNode("span", { class: "lf-dot lf-dot-r" }),
                                    _createElementVNode("span", { class: "lf-dot lf-dot-y" }),
                                    _createElementVNode("span", { class: "lf-dot lf-dot-g" }),
                                    _createElementVNode("div", { class: "lf-url-pill" }, [
                                      _createElementVNode("svg", {
                                        width: "9",
                                        height: "9",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("rect", {
                                          x: "3",
                                          y: "6",
                                          width: "10",
                                          height: "7",
                                          rx: "1.5",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.3"
                                        }),
                                        _createElementVNode("path", {
                                          d: "M5 6V5a3 3 0 016 0v1",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.3",
                                          "stroke-linecap": "round"
                                        })
                                      ]),
                                      _createTextVNode(" yourstore.com ")
                                    ]),
                                    _createElementVNode("div", { class: "lf-bar-icons" }, [
                                      _createElementVNode("svg", {
                                        width: "11",
                                        height: "11",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("circle", {
                                          cx: "7",
                                          cy: "7",
                                          r: "4.5",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.3"
                                        }),
                                        _createElementVNode("path", {
                                          d: "M10.5 10.5L14 14",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.3",
                                          "stroke-linecap": "round"
                                        })
                                      ]),
                                      _createElementVNode("svg", {
                                        width: "11",
                                        height: "11",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("path", {
                                          d: "M2.5 4h11l-1.2 8.5a1.5 1.5 0 01-1.5 1.3H5.2a1.5 1.5 0 01-1.5-1.3z",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.2"
                                        }),
                                        _createElementVNode("path", {
                                          d: "M5.5 4V2.5a.5.5 0 01.5-.5h4a.5.5 0 01.5.5V4",
                                          stroke: "#94a3b8",
                                          "stroke-width": "1.2"
                                        })
                                      ])
                                    ])
                                  ], -1)),
                                  _createElementVNode("div", _hoisted_16, [
                                    _cache[36] || (_cache[36] = _createElementVNode("div", { class: "lf-sf-nav" }, [
                                      _createElementVNode("div", { class: "lf-sf-logo" }),
                                      _createElementVNode("div", { class: "lf-sf-menu" }, [
                                        _createElementVNode("span"),
                                        _createElementVNode("span"),
                                        _createElementVNode("span")
                                      ])
                                    ], -1)),
                                    _cache[37] || (_cache[37] = _createElementVNode("div", { class: "lf-sf-hero-banner" }, [
                                      _createElementVNode("div", { class: "lf-sf-hero-text" }),
                                      _createElementVNode("div", { class: "lf-sf-hero-sub" }),
                                      _createElementVNode("div", { class: "lf-sf-hero-btn" })
                                    ], -1)),
                                    _cache[38] || (_cache[38] = _createElementVNode("div", { class: "lf-sf-section-label" }, null, -1)),
                                    _cache[39] || (_cache[39] = _createElementVNode("div", { class: "lf-sf-grid" }, [
                                      _createElementVNode("div", { class: "lf-sf-card" }, [
                                        _createElementVNode("div", { class: "lf-sf-img lf-sf-img-1" }),
                                        _createElementVNode("div", { class: "lf-sf-line" }),
                                        _createElementVNode("div", { class: "lf-sf-price" })
                                      ]),
                                      _createElementVNode("div", { class: "lf-sf-card" }, [
                                        _createElementVNode("div", { class: "lf-sf-img lf-sf-img-2" }),
                                        _createElementVNode("div", { class: "lf-sf-line" }),
                                        _createElementVNode("div", { class: "lf-sf-price" })
                                      ]),
                                      _createElementVNode("div", { class: "lf-sf-card" }, [
                                        _createElementVNode("div", { class: "lf-sf-img lf-sf-img-3" }),
                                        _createElementVNode("div", { class: "lf-sf-line" }),
                                        _createElementVNode("div", { class: "lf-sf-price" })
                                      ]),
                                      _createElementVNode("div", { class: "lf-sf-card lf-sf-card-hide" }, [
                                        _createElementVNode("div", { class: "lf-sf-img lf-sf-img-4" }),
                                        _createElementVNode("div", { class: "lf-sf-line" }),
                                        _createElementVNode("div", { class: "lf-sf-price" })
                                      ])
                                    ], -1)),
                                    _createElementVNode("div", {
                                      class: _normalizeClass(["lf-dimmer", { 'lf-dim-on': showcaseStep.value >= 3 }])
                                    }, null, 2),
                                    _createVNode(_Transition, { name: "lf-morph" }, {
                                      default: _withCtx(() => [
                                        (lfActiveType.value === 'popup')
                                          ? (_openBlock(), _createElementBlock("div", {
                                              key: "popup",
                                              class: _normalizeClass(["lf-mock lf-mock-popup", { 'sc-visible': showcaseStep.value >= 3 }])
                                            }, [...(_cache[33] || (_cache[33] = [
                                              _createElementVNode("div", { class: "lf-mock-accent lf-mock-accent-purple" }, null, -1),
                                              _createElementVNode("div", { class: "lf-mock-x" }, "✕", -1),
                                              _createElementVNode("div", { class: "lf-mock-body" }, [
                                                _createElementVNode("div", { class: "lf-mock-icon" }, "🎁"),
                                                _createElementVNode("div", { class: "lf-mock-h" }, "Get 10% off"),
                                                _createElementVNode("div", { class: "lf-mock-p" }, " Sign up for exclusive deals "),
                                                _createElementVNode("div", { class: "lf-mock-field" }, [
                                                  _createElementVNode("svg", {
                                                    class: "lf-field-icon",
                                                    width: "10",
                                                    height: "10",
                                                    viewBox: "0 0 16 16",
                                                    fill: "none"
                                                  }, [
                                                    _createElementVNode("rect", {
                                                      x: "1",
                                                      y: "3",
                                                      width: "14",
                                                      height: "10",
                                                      rx: "2",
                                                      stroke: "#94a3b8",
                                                      "stroke-width": "1.3"
                                                    }),
                                                    _createElementVNode("path", {
                                                      d: "M1 5l7 4.5L15 5",
                                                      stroke: "#94a3b8",
                                                      "stroke-width": "1.3"
                                                    })
                                                  ]),
                                                  _createElementVNode("span", null, "Email address")
                                                ]),
                                                _createElementVNode("div", { class: "lf-mock-cta" }, "Claim Discount")
                                              ], -1)
                                            ]))], 2))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_Transition, { name: "lf-morph" }, {
                                      default: _withCtx(() => [
                                        (lfActiveType.value === 'slidein')
                                          ? (_openBlock(), _createElementBlock("div", {
                                              key: "slidein",
                                              class: _normalizeClass(["lf-mock lf-mock-slide", { 'sc-visible': showcaseStep.value >= 3 }])
                                            }, [...(_cache[34] || (_cache[34] = [
                                              _createElementVNode("div", { class: "lf-slide-header" }, [
                                                _createElementVNode("div", { class: "lf-slide-avatar" }, "💬"),
                                                _createElementVNode("div", { class: "lf-slide-info" }, [
                                                  _createElementVNode("div", { class: "lf-mock-h lf-mock-h-sm" }, " Quick question "),
                                                  _createElementVNode("div", { class: "lf-slide-status" }, [
                                                    _createElementVNode("span", { class: "lf-online-dot" }),
                                                    _createTextVNode(" Online now ")
                                                  ])
                                                ]),
                                                _createElementVNode("div", { class: "lf-mock-x lf-mock-x-slide" }, "✕")
                                              ], -1),
                                              _createElementVNode("div", { class: "lf-slide-body" }, [
                                                _createElementVNode("div", { class: "lf-slide-bubble" }, " Want 15% off your first order? 🎉 "),
                                                _createElementVNode("div", { class: "lf-mock-field" }, [
                                                  _createElementVNode("svg", {
                                                    class: "lf-field-icon",
                                                    width: "10",
                                                    height: "10",
                                                    viewBox: "0 0 16 16",
                                                    fill: "none"
                                                  }, [
                                                    _createElementVNode("rect", {
                                                      x: "1",
                                                      y: "3",
                                                      width: "14",
                                                      height: "10",
                                                      rx: "2",
                                                      stroke: "#94a3b8",
                                                      "stroke-width": "1.3"
                                                    }),
                                                    _createElementVNode("path", {
                                                      d: "M1 5l7 4.5L15 5",
                                                      stroke: "#94a3b8",
                                                      "stroke-width": "1.3"
                                                    })
                                                  ]),
                                                  _createElementVNode("span", null, "Enter your email")
                                                ]),
                                                _createElementVNode("div", { class: "lf-mock-cta lf-mock-cta-green" }, " Yes please! ")
                                              ], -1)
                                            ]))], 2))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_Transition, { name: "lf-morph" }, {
                                      default: _withCtx(() => [
                                        (lfActiveType.value === 'banner')
                                          ? (_openBlock(), _createElementBlock("div", {
                                              key: "banner",
                                              class: _normalizeClass(["lf-mock lf-mock-banner", { 'sc-visible': showcaseStep.value >= 3 }])
                                            }, [...(_cache[35] || (_cache[35] = [
                                              _createElementVNode("div", { class: "lf-banner-left" }, [
                                                _createElementVNode("span", { class: "lf-banner-emoji" }, "🔥"),
                                                _createElementVNode("strong", null, "Free shipping today!"),
                                                _createElementVNode("span", { class: "lf-banner-sub" }, "Enter email to unlock")
                                              ], -1),
                                              _createElementVNode("div", { class: "lf-banner-right" }, [
                                                _createElementVNode("div", { class: "lf-mock-field lf-mock-field-inline" }, [
                                                  _createElementVNode("span", null, "Email")
                                                ]),
                                                _createElementVNode("div", { class: "lf-mock-cta lf-mock-cta-dark" }, "Go")
                                              ], -1)
                                            ]))], 2))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _: 1
                                    })
                                  ])
                                ])
                              ], 2),
                              _createElementVNode("div", {
                                class: _normalizeClass(["lf-features", { 'sc-visible': showcaseStep.value >= 4 }])
                              }, [...(_cache[41] || (_cache[41] = [
                                _createElementVNode("div", { class: "lf-chip" }, [
                                  _createElementVNode("span", { class: "lf-chip-dot lf-chip-dot-blue" }),
                                  _createTextVNode(" Linked to browsing data ")
                                ], -1),
                                _createElementVNode("div", { class: "lf-chip" }, [
                                  _createElementVNode("span", { class: "lf-chip-dot lf-chip-dot-purple" }),
                                  _createTextVNode(" Auto discount codes ")
                                ], -1),
                                _createElementVNode("div", { class: "lf-chip" }, [
                                  _createElementVNode("span", { class: "lf-chip-dot lf-chip-dot-green" }),
                                  _createTextVNode(" AI-generated instantly ")
                                ], -1)
                              ]))], 2)
                            ]))
                          : (_unref(store).currentStep?.showcase === 'forms-pick')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                                _createElementVNode("div", _hoisted_18, [
                                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (i) => {
                                    return _createElementVNode("div", {
                                      key: i,
                                      class: _normalizeClass(["sc-particle", `sc-p-${i}`])
                                    }, null, 2)
                                  }), 64))
                                ]),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["fp-hero", { 'sc-visible': showcaseStep.value >= 1 }])
                                }, [...(_cache[42] || (_cache[42] = [
                                  _createElementVNode("div", { class: "sc-badge" }, [
                                    _createElementVNode("span", { class: "sc-badge-dot" }),
                                    _createTextVNode(" AI GENERATED ")
                                  ], -1),
                                  _createElementVNode("h2", { class: "fp-title" }, [
                                    _createTextVNode(" We made you some "),
                                    _createElementVNode("span", { class: "sc-gradient-text" }, "starter forms")
                                  ], -1),
                                  _createElementVNode("p", { class: "fp-subtitle" }, " AI designed 2 options per type to get you going. Preview them on your store and pick your favorites — you can always create new ones or edit these later. ", -1)
                                ]))], 2),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["fp-compare", { 'sc-visible': showcaseStep.value >= 2 }])
                                }, [...(_cache[43] || (_cache[43] = [
                                  _createElementVNode("div", { class: "fp-compare-inner" }, [
                                    _createElementVNode("div", { class: "fp-side fp-side-a" }, [
                                      _createElementVNode("div", { class: "fp-side-badge" }, "A"),
                                      _createElementVNode("div", { class: "fp-mini-form fp-mini-a" }, [
                                        _createElementVNode("div", { class: "fp-mini-accent" }),
                                        _createElementVNode("div", { class: "fp-mini-heading" }),
                                        _createElementVNode("div", { class: "fp-mini-sub" }),
                                        _createElementVNode("div", { class: "fp-mini-field" }),
                                        _createElementVNode("div", { class: "fp-mini-btn" })
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "fp-vs" }, [
                                      _createElementVNode("div", { class: "fp-vs-ring" }, [
                                        _createElementVNode("span", null, "VS")
                                      ]),
                                      _createElementVNode("div", { class: "fp-vs-line" })
                                    ]),
                                    _createElementVNode("div", { class: "fp-side fp-side-b" }, [
                                      _createElementVNode("div", { class: "fp-side-badge fp-side-badge-b" }, "B"),
                                      _createElementVNode("div", { class: "fp-mini-form fp-mini-b" }, [
                                        _createElementVNode("div", { class: "fp-mini-accent fp-mini-accent-dark" }),
                                        _createElementVNode("div", { class: "fp-mini-heading fp-mini-h-dark" }),
                                        _createElementVNode("div", { class: "fp-mini-sub fp-mini-s-dark" }),
                                        _createElementVNode("div", { class: "fp-mini-field fp-mini-f-dark" }),
                                        _createElementVNode("div", { class: "fp-mini-btn fp-mini-btn-warm" })
                                      ])
                                    ])
                                  ], -1)
                                ]))], 2),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["fp-types", { 'sc-visible': showcaseStep.value >= 3 }])
                                }, [...(_cache[44] || (_cache[44] = [
                                  _createElementVNode("div", { class: "fp-type-card fp-type-popup" }, [
                                    _createElementVNode("div", { class: "fp-type-glow" }),
                                    _createElementVNode("div", { class: "fp-type-icon" }, [
                                      _createElementVNode("svg", {
                                        width: "22",
                                        height: "22",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("rect", {
                                          x: "1",
                                          y: "2",
                                          width: "14",
                                          height: "12",
                                          rx: "2",
                                          stroke: "currentColor",
                                          "stroke-width": "1.3"
                                        }),
                                        _createElementVNode("rect", {
                                          x: "4",
                                          y: "5",
                                          width: "8",
                                          height: "6",
                                          rx: "1.5",
                                          stroke: "currentColor",
                                          "stroke-width": "1.1",
                                          opacity: "0.6"
                                        })
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "fp-type-name" }, "Popup"),
                                    _createElementVNode("div", { class: "fp-type-ab" }, [
                                      _createElementVNode("span", { class: "fp-ab fp-ab-a" }, "A"),
                                      _createElementVNode("span", { class: "fp-ab fp-ab-b" }, "B")
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-type-card fp-type-slide" }, [
                                    _createElementVNode("div", { class: "fp-type-glow" }),
                                    _createElementVNode("div", { class: "fp-type-icon" }, [
                                      _createElementVNode("svg", {
                                        width: "22",
                                        height: "22",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("rect", {
                                          x: "1",
                                          y: "2",
                                          width: "14",
                                          height: "12",
                                          rx: "2",
                                          stroke: "currentColor",
                                          "stroke-width": "1.3"
                                        }),
                                        _createElementVNode("rect", {
                                          x: "8",
                                          y: "7",
                                          width: "6",
                                          height: "6",
                                          rx: "1.5",
                                          stroke: "currentColor",
                                          "stroke-width": "1.1",
                                          opacity: "0.6"
                                        })
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "fp-type-name" }, "Slide-in"),
                                    _createElementVNode("div", { class: "fp-type-ab" }, [
                                      _createElementVNode("span", { class: "fp-ab fp-ab-a" }, "A"),
                                      _createElementVNode("span", { class: "fp-ab fp-ab-b" }, "B")
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-type-card fp-type-banner" }, [
                                    _createElementVNode("div", { class: "fp-type-glow" }),
                                    _createElementVNode("div", { class: "fp-type-icon" }, [
                                      _createElementVNode("svg", {
                                        width: "22",
                                        height: "22",
                                        viewBox: "0 0 16 16",
                                        fill: "none"
                                      }, [
                                        _createElementVNode("rect", {
                                          x: "1",
                                          y: "2",
                                          width: "14",
                                          height: "12",
                                          rx: "2",
                                          stroke: "currentColor",
                                          "stroke-width": "1.3"
                                        }),
                                        _createElementVNode("rect", {
                                          x: "2.5",
                                          y: "3.5",
                                          width: "11",
                                          height: "3",
                                          rx: "1",
                                          stroke: "currentColor",
                                          "stroke-width": "1.1",
                                          opacity: "0.6"
                                        })
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "fp-type-name" }, "Banner"),
                                    _createElementVNode("div", { class: "fp-type-ab" }, [
                                      _createElementVNode("span", { class: "fp-ab fp-ab-a" }, "A"),
                                      _createElementVNode("span", { class: "fp-ab fp-ab-b" }, "B")
                                    ])
                                  ], -1)
                                ]))], 2),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["fp-flow", { 'sc-visible': showcaseStep.value >= 4 }])
                                }, [...(_cache[45] || (_cache[45] = [
                                  _createElementVNode("div", { class: "fp-flow-step" }, [
                                    _createElementVNode("div", { class: "fp-flow-num" }, "1"),
                                    _createElementVNode("div", { class: "fp-flow-text" }, [
                                      _createElementVNode("strong", null, "Open your store"),
                                      _createElementVNode("span", null, "See forms live on your site")
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-flow-connector" }, [
                                    _createElementVNode("svg", {
                                      width: "20",
                                      height: "12",
                                      viewBox: "0 0 20 12"
                                    }, [
                                      _createElementVNode("path", {
                                        d: "M0 6h16M12 1l5 5-5 5",
                                        stroke: "#c4b5fd",
                                        "stroke-width": "1.5",
                                        fill: "none",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      })
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-flow-step" }, [
                                    _createElementVNode("div", { class: "fp-flow-num fp-flow-num-2" }, "2"),
                                    _createElementVNode("div", { class: "fp-flow-text" }, [
                                      _createElementVNode("strong", null, "Compare A & B"),
                                      _createElementVNode("span", null, "Toggle between options")
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-flow-connector" }, [
                                    _createElementVNode("svg", {
                                      width: "20",
                                      height: "12",
                                      viewBox: "0 0 20 12"
                                    }, [
                                      _createElementVNode("path", {
                                        d: "M0 6h16M12 1l5 5-5 5",
                                        stroke: "#c4b5fd",
                                        "stroke-width": "1.5",
                                        fill: "none",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      })
                                    ])
                                  ], -1),
                                  _createElementVNode("div", { class: "fp-flow-step" }, [
                                    _createElementVNode("div", { class: "fp-flow-num fp-flow-num-3" }, "3"),
                                    _createElementVNode("div", { class: "fp-flow-text" }, [
                                      _createElementVNode("strong", null, "Pick your fav"),
                                      _createElementVNode("span", null, "One click and you're done")
                                    ])
                                  ], -1)
                                ]))], 2),
                                (walkthroughFormsLoading.value)
                                  ? (_openBlock(), _createElementBlock("div", {
                                      key: 0,
                                      class: _normalizeClass(["fp-loading", { 'sc-visible': showcaseStep.value >= 1 }])
                                    }, [...(_cache[46] || (_cache[46] = [
                                      _createElementVNode("div", { class: "lf-gen-spinner" }, null, -1),
                                      _createElementVNode("span", null, "AI is designing your form options… this takes a moment", -1)
                                    ]))], 2))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: _normalizeClass(["fp-skip-section", { 'sc-visible': showcaseStep.value >= 4 }])
                                }, [
                                  _createElementVNode("button", {
                                    class: "fp-skip-btn",
                                    onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(store).next()))
                                  }, " I'll do this later "),
                                  _cache[47] || (_cache[47] = _createElementVNode("p", { class: "fp-skip-hint" }, " You can pick forms anytime from this page ", -1))
                                ], 2)
                              ]))
                            : (_unref(store).currentStep?.showcase === 'welcome')
                              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                  _cache[55] || (_cache[55] = _createElementVNode("div", { class: "wc-check-wrap" }, [
                                    _createElementVNode("svg", {
                                      class: "wc-check-svg",
                                      viewBox: "0 0 64 64"
                                    }, [
                                      _createElementVNode("circle", {
                                        class: "wc-check-bg",
                                        cx: "32",
                                        cy: "32",
                                        r: "29"
                                      }),
                                      _createElementVNode("circle", {
                                        class: "wc-check-ring",
                                        cx: "32",
                                        cy: "32",
                                        r: "29"
                                      }),
                                      _createElementVNode("path", {
                                        class: "wc-check-tick",
                                        d: "M20 32l8 8 16-16"
                                      })
                                    ])
                                  ], -1)),
                                  (_unref(demoStore).isDemoMode)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _cache[48] || (_cache[48] = _createElementVNode("div", { class: "wc-demo-badge" }, "INTERACTIVE DEMO", -1)),
                                        _cache[49] || (_cache[49] = _createElementVNode("h2", { class: "wc-title" }, [
                                          _createTextVNode(" See "),
                                          _createElementVNode("span", { class: "wc-title-em" }, "MyUser AI"),
                                          _createTextVNode(" in action ")
                                        ], -1)),
                                        _cache[50] || (_cache[50] = _createElementVNode("p", { class: "wc-subtitle" }, " Walk through a real store with sample data. You'll simulate customer behavior and watch the AI respond live. ", -1))
                                      ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _cache[51] || (_cache[51] = _createElementVNode("h2", { class: "wc-title" }, [
                                          _createTextVNode(" Your AI marketing engine "),
                                          _createElementVNode("span", { class: "wc-title-em" }, "is ready")
                                        ], -1)),
                                        _cache[52] || (_cache[52] = _createElementVNode("p", { class: "wc-subtitle" }, " See how customer actions become personalized messages — automatically. ", -1))
                                      ], 64)),
                                  _cache[56] || (_cache[56] = _createElementVNode("div", { class: "wc-pipeline" }, [
                                    _createElementVNode("div", { class: "wc-step wc-s1" }, [
                                      _createElementVNode("div", { class: "wc-step-dot wc-dot-blue" }),
                                      _createElementVNode("div", { class: "wc-step-content" }, [
                                        _createElementVNode("span", { class: "wc-step-emoji" }, "🛒"),
                                        _createElementVNode("span", { class: "wc-step-name" }, "Customer acts")
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "wc-connector wc-c1" }, [
                                      _createElementVNode("div", { class: "wc-conn-line" }),
                                      _createElementVNode("div", { class: "wc-conn-pulse" })
                                    ]),
                                    _createElementVNode("div", { class: "wc-step wc-s2" }, [
                                      _createElementVNode("div", { class: "wc-step-dot wc-dot-purple" }),
                                      _createElementVNode("div", { class: "wc-step-content" }, [
                                        _createElementVNode("span", { class: "wc-step-emoji" }, "🧠"),
                                        _createElementVNode("span", { class: "wc-step-name" }, "AI decides")
                                      ])
                                    ]),
                                    _createElementVNode("div", { class: "wc-connector wc-c2" }, [
                                      _createElementVNode("div", { class: "wc-conn-line" }),
                                      _createElementVNode("div", { class: "wc-conn-pulse" })
                                    ]),
                                    _createElementVNode("div", { class: "wc-step wc-s3" }, [
                                      _createElementVNode("div", { class: "wc-step-dot wc-dot-green" }),
                                      _createElementVNode("div", { class: "wc-step-content" }, [
                                        _createElementVNode("span", { class: "wc-step-emoji" }, "✉️"),
                                        _createElementVNode("span", { class: "wc-step-name" }, "Message sent")
                                      ])
                                    ])
                                  ], -1)),
                                  _createElementVNode("div", _hoisted_20, [
                                    _cache[53] || (_cache[53] = _createElementVNode("span", { class: "wc-meta-pill" }, "⏱ ~1 min", -1)),
                                    _cache[54] || (_cache[54] = _createElementVNode("span", { class: "wc-meta-pill" }, "12 interactive steps", -1)),
                                    (_unref(demoStore).isDemoMode)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_21, "🧪 Sample data"))
                                      : _createCommentVNode("", true)
                                  ])
                                ]))
                              : (_unref(store).currentStep?.showcase === 'simulate-interactive')
                                ? (_openBlock(), _createBlock(WalkthroughSimulate, {
                                    key: 5,
                                    ref_key: "simRef",
                                    ref: simRef
                                  }, null, 512))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                                    _createElementVNode("div", _hoisted_22, _toDisplayString(_unref(store).currentStep?.icon), 1),
                                    _createElementVNode("div", _hoisted_23, [
                                      _createElementVNode("h3", _hoisted_24, _toDisplayString(_unref(store).currentStep?.title), 1),
                                      _createElementVNode("p", _hoisted_25, _toDisplayString(tooltipBodyOverride.value || _unref(store).currentStep?.body), 1),
                                      (_unref(store).currentStep?.id === 'dashboard-message-frequency')
                                        ? (_openBlock(), _createElementBlock("div", {
                                            key: 0,
                                            class: _normalizeClass(["drag-objective", { 'objective-complete': rangeDragged.value }])
                                          }, [
                                            _createElementVNode("span", _hoisted_26, _toDisplayString(rangeDragged.value ? '✅' : '⬜'), 1),
                                            _createElementVNode("span", _hoisted_27, _toDisplayString(rangeDragged.value
                      ? 'Range adjusted!'
                      : 'Try dragging the green band'), 1)
                                          ], 2))
                                        : _createCommentVNode("", true)
                                    ])
                                  ], 64)),
                    _createElementVNode("div", _hoisted_28, [
                      _createElementVNode("button", {
                        class: "tooltip-btn tooltip-btn-skip",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(store).dismiss()))
                      }, " Skip tour "),
                      (_unref(store).currentStepIndex > 0)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            class: "tooltip-btn tooltip-btn-back",
                            onClick: _cache[5] || (_cache[5] = ($event: any) => (simIsInSubView.value ? simBackToMenu() : _unref(store).prev()))
                          }, [...(_cache[57] || (_cache[57] = [
                            _createTextVNode(" ← ", -1),
                            _createElementVNode("kbd", { class: "key-hint" }, "←", -1)
                          ]))]))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        class: "tooltip-btn tooltip-btn-next",
                        onClick: _cache[6] || (_cache[6] = ($event: any) => (simStepNeedsMore.value ? simBackToMenu() : handleNext()))
                      }, [
                        _createTextVNode(_toDisplayString(simActionLabel.value) + " ", 1),
                        (!simStepNeedsMore.value)
                          ? (_openBlock(), _createElementBlock("kbd", _hoisted_29, "→"))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_30, _toDisplayString(_unref(store).visibleStepNumber) + " / " + _toDisplayString(_unref(store).totalSteps), 1)
                  ], 6)), [
                    [_vShow, !isOffTrack.value && tooltipReady.value]
                  ])
                ]),
                _: 1
              })
            ], 2)), [
              [_vShow, !modalOpen.value]
            ])
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "celebrate-fade" }, {
      default: _withCtx(() => [
        (showCelebration.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "celebration-overlay",
              onClick: _withModifiers(closeCelebration, ["self"])
            }, [
              _createElementVNode("div", _hoisted_31, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(50, (i) => {
                  return _createElementVNode("div", {
                    key: i,
                    class: "confetti",
                    style: _normalizeStyle(confettiStyle(i))
                  }, null, 4)
                }), 64))
              ]),
              _createElementVNode("div", {
                class: _normalizeClass(["celebration-card", {
            'celebration-card-demo-shell': _unref(demoStore).isDemoMode,
            'celebration-card-immersive': !_unref(demoStore).isDemoMode
          }])
              }, [
                (_unref(demoStore).isDemoMode)
                  ? (_openBlock(), _createBlock(DemoCelebrationV3, {
                      key: 0,
                      onInstall: goToShopifyInstall,
                      onExplore: closeCelebration,
                      onReplay: replayTour
                    }))
                  : (_openBlock(), _createBlock(WalkthroughCelebration, {
                      key: 1,
                      loading: celebrationLoading.value,
                      "is-active": projectIsActive.value,
                      activating: activatingProject.value,
                      activated: activationSuccess.value,
                      onClose: closeCelebration,
                      onActivate: activateProject,
                      onReplay: replayTour
                    }, null, 8, ["loading", "is-active", "activating", "activated"]))
              ], 2)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "offtrack-slide" }, {
      default: _withCtx(() => [
        (_unref(store).isActive && isOffTrack.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
              _cache[58] || (_cache[58] = _createElementVNode("span", { class: "offtrack-icon" }, "📍", -1)),
              _cache[59] || (_cache[59] = _createElementVNode("span", { class: "offtrack-text" }, "Tour paused — you navigated away", -1)),
              _createElementVNode("button", {
                class: "offtrack-btn",
                onClick: returnToTour
              }, " Back to " + _toDisplayString(offTrackPageName.value) + " → ", 1),
              _createElementVNode("button", {
                class: "offtrack-dismiss",
                onClick: _cache[7] || (_cache[7] = ($event: any) => (_unref(store).dismiss())),
                title: "End tour"
              }, " ✕ ")
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "walkthrough-fade" }, {
      default: _withCtx(() => [
        (awaitingUserGeneration.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_33, [...(_cache[60] || (_cache[60] = [
              _createElementVNode("div", { class: "wt-generating-card" }, [
                _createElementVNode("div", { class: "wt-generating-spinner" }),
                _createElementVNode("h3", { class: "wt-generating-title" }, "Setting up your tour"),
                _createElementVNode("p", { class: "wt-generating-body" }, " We're creating a sample customer so you can see how everything works. Just a moment… ")
              ], -1)
            ]))]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})